import api from './api'
import { ordinaryDocumentConsents } from './workerDocumentConsent'
import type {
  AccessContext,
  AccessibleLocation,
  AssociatedClientCompanyPayload,
  AssociatedClientCompanyResult,
  GlobalUserDirectoryEntry,
  GlobalUserDirectoryResult,
  ListQuery,
  Organization,
  OrganizationBranding,
  OrganizationBrandingPayload,
  OrganizationPayload,
  OrganizationRelationship,
  OrganizationConnectionInvite,
  OrganizationRelationshipPolicyPayload,
  OrganizationRelationshipPayload,
  OrganizationRelationshipUpdatePayload,
  OrganizationType,
  OrganizationUnit,
  OrganizationInviteSummary,
  OrganizationUnitPayload,
  OrganizationUnitUpdatePayload,
  OrganizationUserInvitePayload,
  OrganizationUserRolePayload,
  OrganizationUserSummary,
  OrganizationUserUpdatePayload,
  OrganizationUpdatePayload,
  FreeAgentWorkerProfile,
  Job,
  JobApplication,
  JobApplicationPayload,
  JobApplicationStatus,
  JobAssignment,
  JobAssignmentPayload,
  JobAssignmentStatusPayload,
  JobAssignmentUpdatePayload,
  OfflinePunchReview,
  OperationsReport,
  OperationsReportFilters,
  JobPayload,
  JobShift,
  JobShiftPayload,
  JobShiftUpdatePayload,
  JobShiftWorkerMatch,
  JobStatusPayload,
  JobUpdatePayload,
  JobVisibilityRule,
  JobVisibilityRulePayload,
  WorkPostingPayload,
  WorkPostingResult,
  ScopedRoleAssignment,
  ScopedRoleAssignmentPayload,
  ScopedRoleAssignmentUpdatePayload,
  TenantRoleSummary,
  AdminCapabilitySummary,
  ScheduleCalendarEvent,
  ScheduleCalendarFilters,
  ScheduleShiftConflict,
  ScheduleShiftDetail,
  ScheduleShift,
  ScheduleShiftFilters,
    TimesheetApprovalHistoryRow,
    TimesheetApprovalQueueRow,
    AdminTimesheetAdjustPayload,
    AdminTimesheetResolvePayload,
    TimesheetExportFilters,
    ReportingExportHistoryRow,
  SharingAgreement,
  SharingAgreementPayload,
  SharingAgreementResource,
  SharingAgreementResourcePayload,
  SharingAgreementUpdatePayload,
  WorkerDocumentConsent,
  WorkerDocumentConsentPayload,
  WorkerDocumentConsentUpdatePayload,
  WorkerDocumentRisk,
  WorkerDocumentSkillLinkPayload,
  WorkerOrgRelationship,
  WorkerInvitePayload,
  WorkerProfileDocument,
  WorkerProfileDetail,
  WorkerReliabilityProfile,
  WorkerAttendanceEvent,
  WorkerSupervisorReview,
  WorkerAvailabilitySummary,
  WorkerAvailabilityWindow,
  WorkerUnavailabilityWindow,
  ShiftApplication,
  WorkerProfileVisibilityPayload,
  WorkerRelationshipPayload,
  WorkerRelationshipUpdatePayload,
  Skill,
  SkillPayload,
  WorkerSkill,
  LiveWorkerLocationDto,
  WorkerBreadcrumbPointDto,
  WorkerBreadcrumbTrailDto,
  WorkerBreadcrumbQuery,
  PaginatedResponse,
  Message,
  MessageRecipient,
  MessageThread,
  CreateMessageThreadRequest,
  SendMessageRequest,
  WhatsappNotificationStatus,
  WhatsappProviderSettingsPayload,
  TenantOnboardingState,
  OnboardingStepKey,
  OnboardingReadiness,
  AccessTenant,
} from '../types/access'
import type { IndustryType } from '../domain/industryPresets'

type ListParams = ListQuery & Record<string, unknown>

export type NormalizedApiError = {
  status?: number
  message: string
  code?: string
  details?: unknown
  fieldErrors?: Array<{ field?: string; message: string }>
}

export const normalizeApiError = (error: unknown, fallback = 'Request failed.'): NormalizedApiError => {
  const err = error as {
    response?: { status?: number; data?: any }
    message?: string
  }
  const data = err?.response?.data || {}
  const rawErrors = data.errors || data.fieldErrors || data.details?.errors || []
  const fieldErrors = Array.isArray(rawErrors)
    ? rawErrors.map((entry: any) => ({
      field: entry.field || entry.path || entry.name,
      message: String(entry.message || entry.error || entry),
    }))
    : []
  const detailText = fieldErrors.length
    ? fieldErrors.map((entry) => [entry.field, entry.message].filter(Boolean).join(': ')).join('; ')
    : ''
  const structuredError = data.error && typeof data.error === 'object' ? data.error : null
  const message = String(data.message || structuredError?.message || data.error || err?.message || fallback)
  return {
    status: err?.response?.status,
    message: detailText && !message.includes(detailText) ? `${message} ${detailText}` : message,
    code: structuredError?.code || data.code,
    details: data.details,
    fieldErrors,
  }
}

export const apiErrorMessage = (error: unknown, fallback = 'Request failed.') => normalizeApiError(error, fallback).message

const firstValue = <T,>(...values: Array<T | null | undefined>) => values.find((value) => value !== null && value !== undefined)

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const isValidLatitude = (value: unknown): value is number => {
  const numeric = toNumberOrNull(value)
  return numeric !== null && numeric >= -90 && numeric <= 90
}

const isValidLongitude = (value: unknown): value is number => {
  const numeric = toNumberOrNull(value)
  return numeric !== null && numeric >= -180 && numeric <= 180
}

const normalizeCoordinatePair = (latValue: unknown, lonValue: unknown): { latitude: number; longitude: number } | null => {
  let latitude = toNumberOrNull(latValue)
  let longitude = toNumberOrNull(lonValue)
  if (
    latitude !== null
    && longitude !== null
    && !isValidLatitude(latitude)
    && isValidLatitude(longitude)
    && isValidLongitude(latitude)
  ) {
    const swappedLatitude = longitude
    longitude = latitude
    latitude = swappedLatitude
  }
  if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) return null
  return { latitude, longitude }
}

const asArray = (value: unknown): any[] => Array.isArray(value) ? value : []

const defaultPagination = (total: number) => ({
  page: 1,
  pageSize: total || 25,
  total,
  totalPages: 1,
  hasNextPage: false,
  hasPreviousPage: false,
})

const unwrapPaginated = <T,>(payload: unknown, key: string): PaginatedResponse<T> => {
  const body = (payload || {}) as any
  if (Array.isArray(body.items) && body.pagination) return body as PaginatedResponse<T>
  const items = (body[key] || body.data || []) as T[]
  return {
    items,
    pagination: defaultPagination(items.length),
    sort: { sortDir: 'asc' },
    filters: {},
  }
}

const normalizeReliability = (row: any): WorkerReliabilityProfile | null => {
  if (!row) return null
  const workerProfileId = String(firstValue(row.workerProfileId, row.worker_profile_id, '') || '')
  return {
    ...row,
    workerProfileId,
    worker_profile_id: workerProfileId,
    reliabilityScore: firstValue(row.reliabilityScore, row.reliability_score, null),
    reliability_score: firstValue(row.reliability_score, row.reliabilityScore, null),
    attendanceScore: firstValue(row.attendanceScore, row.attendance_score, row.attendancePercentage, row.attendance_percentage, null),
    attendancePercentage: firstValue(row.attendancePercentage, row.attendance_percentage, row.attendanceScore, row.attendance_score, null),
    attendance_score: firstValue(row.attendance_score, row.attendanceScore, row.attendancePercentage, row.attendance_percentage, null),
    punctualityScore: firstValue(row.punctualityScore, row.punctuality_score, row.punctualityPercentage, row.punctuality_percentage, null),
    punctualityPercentage: firstValue(row.punctualityPercentage, row.punctuality_percentage, row.punctualityScore, row.punctuality_score, null),
    punctuality_score: firstValue(row.punctuality_score, row.punctualityScore, row.punctualityPercentage, row.punctuality_percentage, null),
    completionScore: firstValue(row.completionScore, row.completion_score, row.completionPercentage, row.completion_percentage, null),
    completionPercentage: firstValue(row.completionPercentage, row.completion_percentage, row.completionScore, row.completion_score, null),
    completion_score: firstValue(row.completion_score, row.completionScore, row.completionPercentage, row.completion_percentage, null),
    supervisorRating: firstValue(row.supervisorRating, row.supervisor_rating, null),
    supervisor_rating: firstValue(row.supervisor_rating, row.supervisorRating, null),
  }
}

export const normalizeLiveWorkerPins = (payload: unknown): LiveWorkerLocationDto[] => {
  const body = payload as any
  const data = body?.data
  const rows = asArray(data)
    .concat(asArray(data?.workers))
    .concat(asArray(data?.pins))
    .concat(asArray(body?.workers))
    .concat(asArray(body?.pins))
  return rows.map((row: any) => {
    const point = normalizeCoordinatePair(firstValue(row.location?.latitude, row.latitude, row.lat), firstValue(row.location?.longitude, row.longitude, row.lon, row.lng))
    const contact = row.contact || {}
    const presence = row.presence || {}
    const shift = row.shift || null
    return {
      workerProfileId: String(firstValue(row.workerProfileId, row.worker_profile_id, '') || ''),
      worker_profile_id: String(firstValue(row.workerProfileId, row.worker_profile_id, '') || ''),
      globalWorkerProfileId: String(firstValue(row.globalWorkerProfileId, row.global_worker_profile_id, row.workerProfileId, row.worker_profile_id, '') || ''),
      global_worker_profile_id: String(firstValue(row.global_worker_profile_id, row.globalWorkerProfileId, row.worker_profile_id, row.workerProfileId, '') || ''),
      workerName: String(firstValue(row.workerName, row.worker_name, row.name, row.displayName, row.display_name, '') || ''),
      worker_name: String(firstValue(row.workerName, row.worker_name, row.name, row.displayName, row.display_name, '') || ''),
      name: String(firstValue(row.name, row.workerName, row.worker_name, row.displayName, row.display_name, '') || ''),
      userId: String(firstValue(row.userId, row.user_id, row.workerUserId, row.worker_user_id, '') || ''),
      workerUserId: firstValue(row.workerUserId, row.worker_user_id, row.userId, row.user_id, null),
      profilePhotoUrl: firstValue(row.profilePhotoUrl, null),
      displayName: String(firstValue(row.displayName, row.display_name, row.workerName, row.worker_name, row.name, 'Worker') || 'Worker'),
      display_name: String(firstValue(row.displayName, row.display_name, row.workerName, row.worker_name, row.name, 'Worker') || 'Worker'),
      status: String(firstValue(presence.status, row.status, row.currentPunchStatus, row.current_punch_status, '') || ''),
      phone: firstValue(contact.phone, row.phone, row.workerPhone, row.worker_phone, null),
      workerPhone: firstValue(row.workerPhone, row.worker_phone, row.phone, null),
      worker_phone: firstValue(row.workerPhone, row.worker_phone, row.phone, null),
      email: firstValue(contact.email, row.email, row.workerEmail, row.worker_email, null),
      workerEmail: firstValue(row.workerEmail, row.worker_email, row.email, null),
      worker_email: firstValue(row.workerEmail, row.worker_email, row.email, null),
      role: firstValue(row.role, row.jobTitle, row.job_title, row.assignmentLabel, row.assignment_label, null),
      jobTitle: firstValue(shift?.jobTitle, row.jobTitle, row.job_title, row.role, null),
      job_title: firstValue(row.jobTitle, row.job_title, row.role, null),
      assignmentLabel: firstValue(row.assignmentLabel, row.assignment_label, row.jobTitle, row.job_title, null),
      assignment_label: firstValue(row.assignmentLabel, row.assignment_label, row.jobTitle, row.job_title, null),
      jobAssignmentId: firstValue(shift?.assignmentId, row.jobAssignmentId, row.job_assignment_id, null),
      jobShiftId: firstValue(shift?.jobShiftId, shift?.shiftId, row.jobShiftId, row.job_shift_id, null),
      locationId: String(firstValue(shift?.locationId, row.locationId, row.location_id, '') || ''),
      locationName: firstValue(shift?.locationName, row.locationName, row.location_name, null),
      lat: point?.latitude ?? null,
      lon: point?.longitude ?? null,
      latitude: point?.latitude,
      longitude: point?.longitude,
      accuracyMeters: toNumberOrNull(firstValue(row.location?.accuracyMeters, row.accuracyMeters, row.accuracy_meters, row.accuracy)),
      recordedAt: String(firstValue(row.location?.recordedAt, row.recordedAt, row.recorded_at, row.capturedAt, row.captured_at, row.lastSeenAt, row.last_seen_at, '') || ''),
      capturedAt: String(firstValue(row.capturedAt, row.captured_at, row.recordedAt, row.recorded_at, row.lastSeenAt, row.last_seen_at, '') || ''),
      lastSeenAt: String(firstValue(row.lastSeenAt, row.last_seen_at, row.recordedAt, row.recorded_at, row.capturedAt, row.captured_at, '') || ''),
      punchInAt: firstValue(presence.punchedInAt, row.punchInAt, row.punch_in_at, row.punchedInAt, row.punched_in_at, row.timeIn, row.time_in, null),
      punchedInAt: firstValue(row.punchedInAt, row.punched_in_at, row.punchInAt, row.punch_in_at, row.timeIn, row.time_in, null),
      punched_in_at: firstValue(row.punchedInAt, row.punched_in_at, row.punchInAt, row.punch_in_at, row.timeIn, row.time_in, null),
      timeIn: firstValue(row.timeIn, row.time_in, row.punchInAt, row.punch_in_at, null),
      time_in: firstValue(row.timeIn, row.time_in, row.punchInAt, row.punch_in_at, null),
      startedAt: firstValue(shift?.startsAt, row.startedAt, row.started_at, row.shiftStartedAt, row.shift_started_at, null),
      started_at: firstValue(row.startedAt, row.started_at, row.shiftStartedAt, row.shift_started_at, null),
      shiftStartedAt: firstValue(row.shiftStartedAt, row.shift_started_at, row.startedAt, row.started_at, null),
      shift_started_at: firstValue(row.shiftStartedAt, row.shift_started_at, row.startedAt, row.started_at, null),
      scheduleStatus: firstValue(row.scheduleStatus, row.schedule_status, row.timeState, row.time_state, null),
      schedule_status: firstValue(row.scheduleStatus, row.schedule_status, row.timeState, row.time_state, null),
      timeState: firstValue(row.timeState, row.time_state, row.scheduleStatus, row.schedule_status, null),
      time_state: firstValue(row.timeState, row.time_state, row.scheduleStatus, row.schedule_status, null),
      isLate: Boolean(firstValue(row.isLate, row.is_late, row.late, false)),
      late: Boolean(firstValue(row.late, row.isLate, row.is_late, false)),
      lastPunchAt: firstValue(row.lastPunchAt, row.last_punch_at, row.statusChangedAt, row.status_changed_at, null),
      last_punch_at: firstValue(row.lastPunchAt, row.last_punch_at, row.statusChangedAt, row.status_changed_at, null),
      onTheWayAt: firstValue(presence.onTheWayAt, row.onTheWayAt, row.on_the_way_at, null),
      on_the_way_at: firstValue(row.on_the_way_at, row.onTheWayAt, null),
      onTheWayEtaMinutes: toNumberOrNull(firstValue(presence.etaMinutes, row.onTheWayEtaMinutes, row.on_the_way_eta_minutes, null)),
      on_the_way_eta_minutes: toNumberOrNull(firstValue(row.on_the_way_eta_minutes, row.onTheWayEtaMinutes, null)),
      onTheWayDistanceMeters: toNumberOrNull(firstValue(presence.etaDistanceMeters, row.onTheWayDistanceMeters, row.on_the_way_distance_meters, null)),
      on_the_way_distance_meters: toNumberOrNull(firstValue(row.on_the_way_distance_meters, row.onTheWayDistanceMeters, null)),
      onTheWayEtaLabel: firstValue(presence.etaLabel, row.onTheWayEtaLabel, row.on_the_way_eta_label, null),
      on_the_way_eta_label: firstValue(row.on_the_way_eta_label, row.onTheWayEtaLabel, null),
      lifecycleStatus: firstValue(presence.lifecycleStatus, row.lifecycleStatus, row.lifecycle_status, null),
      lifecycleLabel: firstValue(presence.lifecycleLabel, row.lifecycleLabel, row.lifecycle_label, null),
      attendanceException: firstValue(presence.attendanceException, row.attendanceException, row.attendance_exception, null),
      lifecycleUpdatedAt: firstValue(presence.lifecycleUpdatedAt, row.lifecycleUpdatedAt, row.lifecycle_updated_at, null),
      eta: firstValue(presence.eta, row.eta, null),
      isStale: Boolean(firstValue(row.location?.isStale, row.isStale, row.is_stale, row.stale, false)),
      missingGps: Boolean(firstValue(row.location?.missingGps, row.missingGps, row.missing_gps, point == null)),
      source: firstValue(row.location?.source, row.source, null),
      locationFallback: Boolean(firstValue(row.locationFallback, row.location_fallback, row.source === 'location_fallback')),
      contact: row.contact,
      location: row.location,
      presence: row.presence,
      shift: row.shift,
      actions: row.actions,
    }
  }).filter((row) => row.workerProfileId)
}

export const normalizeWorkerBreadcrumbs = (payload: unknown): WorkerBreadcrumbTrailDto => {
  const body = payload as any
  const data = body?.data
  const pointsPayload = asArray(data)
    .concat(asArray(data?.points))
    .concat(asArray(data?.breadcrumbs))
    .concat(asArray(body?.points))
    .concat(asArray(body?.breadcrumbs))
  const points: WorkerBreadcrumbPointDto[] = pointsPayload.map((point: any) => {
    const coordinate = normalizeCoordinatePair(firstValue(point.latitude, point.lat), firstValue(point.longitude, point.lon, point.lng))
    if (!coordinate) return null
    return {
      lat: coordinate.latitude,
      lon: coordinate.longitude,
      lng: coordinate.longitude,
      latitude: coordinate.latitude,
      longitude: coordinate.longitude,
      accuracyMeters: toNumberOrNull(firstValue(point.accuracyMeters, point.accuracy_meters, point.accuracy)),
      recordedAt: String(firstValue(point.recordedAt, point.recorded_at, point.capturedAt, point.captured_at, '') || ''),
      capturedAt: String(firstValue(point.capturedAt, point.captured_at, point.recordedAt, point.recorded_at, '') || ''),
      source: firstValue(point.source, null),
      eventType: firstValue(point.eventType, point.event_type, null),
      sequence: firstValue(point.sequence, point.order, point.index, null),
      order: firstValue(point.order, point.sequence, point.index, null),
      index: firstValue(point.index, point.sequence, point.order, null),
    }
  }).filter(Boolean).sort((left: any, right: any) => {
    const leftTime = Date.parse(left.capturedAt || left.recordedAt || '')
    const rightTime = Date.parse(right.capturedAt || right.recordedAt || '')
    return (Number.isFinite(leftTime) ? leftTime : 0) - (Number.isFinite(rightTime) ? rightTime : 0)
  }) as WorkerBreadcrumbPointDto[]
  return {
    workerProfileId: String(firstValue(data?.workerProfileId, data?.worker_profile_id, body?.workerProfileId, body?.worker_profile_id, '') || ''),
    displayName: String(firstValue(data?.displayName, data?.display_name, body?.displayName, body?.display_name, '') || ''),
    locationId: firstValue(data?.locationId, data?.location_id, body?.locationId, body?.location_id, null),
    from: String(firstValue(data?.from, body?.from, '') || ''),
    to: String(firstValue(data?.to, body?.to, '') || ''),
    points,
    rawPointCount: pointsPayload.length,
  }
}

export const getAdminLocationLiveWorkers = async (locationId: string, params: Pick<WorkerBreadcrumbQuery, 'includeLatestOnly' | 'limit'> = {}, signal?: AbortSignal): Promise<LiveWorkerLocationDto[]> => {
  const response = await api.get(`/api/admin/locations/${locationId}/live-workers`, { params, signal, headers: { 'x-role': 'corporate' } })
  return normalizeLiveWorkerPins(response.data)
}

export const getAdminWorkerBreadcrumbs = async (workerProfileId: string, locationId?: string, params: WorkerBreadcrumbQuery = {}): Promise<WorkerBreadcrumbTrailDto> => {
  const response = await api.get(`/api/admin/workers/${workerProfileId}/breadcrumbs`, {
    params: { ...params, locationId: locationId || params.locationId },
    headers: { 'x-role': 'corporate' },
  })
  return normalizeWorkerBreadcrumbs(response.data)
}

export const getAccessContext = async (tenantId?: string, signal?: AbortSignal): Promise<AccessContext> => {
  const response = await api.get('/api/access/my-context', { signal, ...(tenantId ? { headers: { 'x-tenant-id': tenantId } } : {}) })
  const body = response.data || {}
  const data = body.data || body
  const visibleOrganizations = data.visibleOrganizations || data.organizations || body.visibleOrganizations || []
  const isSystemSuperAdmin = Boolean(data.isSystemSuperAdmin || body.isSystemSuperAdmin)
  return {
    ...body,
    ...data,
    tenantId: body.tenantId || data.tenantId || data.tenant?.id || '',
    selectedTenant: data.selectedTenant || body.selectedTenant || data.tenant || body.tenant || null,
    availableTenants: data.availableTenants || body.availableTenants || [],
    tenantMemberships: data.tenantMemberships || data.memberships || body.tenantMemberships || [],
    memberships: data.memberships || data.tenantMemberships || body.tenantMemberships || [],
    corporateProfile: data.corporateProfile || body.corporateProfile || null,
    visibleOrganizations,
    organizations: data.organizations || data.visibleOrganizations || body.visibleOrganizations || [],
    currentOrganization: isSystemSuperAdmin ? data.currentOrganization || body.currentOrganization || null : data.currentOrganization || body.currentOrganization || visibleOrganizations[0] || null,
    effectivePermissionKeys: data.effectivePermissionKeys || data.permissions || body.effectivePermissionKeys || [],
    permissions: data.permissions || data.effectivePermissionKeys || body.effectivePermissionKeys || [],
    systemPermissions: data.systemPermissions || body.systemPermissions || [],
    tenantPermissions: data.tenantPermissions || body.tenantPermissions || [],
    organizationRelationships: data.organizationRelationships || body.organizationRelationships || [],
    relationshipAccess: (data.relationshipAccess || body.relationshipAccess || []).map((entry: any) => ({
      ...entry,
      grantedLocations: (Array.isArray(entry.locations) ? entry.locations : (Array.isArray(entry.grantedLocations) ? entry.grantedLocations : [])).map((loc: any) => ({
        id: loc.id || loc.locationId,
        locationId: loc.locationId || loc.id,
        locationTenantId: loc.locationTenantId || loc.location_tenant_id || '',
        locationKey: loc.locationKey || loc.location_key || null,
        name: loc.name || loc.locationName || loc.location_name || '',
        timezone: loc.timezone || null,
        ownerOrgType: loc.ownerOrgType || loc.owner_org_type || 'company',
        companyTenantId: loc.companyTenantId || loc.company_tenant_id || null,
        companyOrgId: loc.companyOrgId || loc.company_org_id || null,
        agencyTenantId: loc.agencyTenantId || loc.agency_tenant_id || null,
        agencyOrgId: loc.agencyOrgId || loc.agency_org_id || null,
        ownerOrgId: loc.ownerOrgId || loc.owner_org_id || null,
        canView: Boolean(loc.canView ?? loc.can_view ?? true),
        canStaff: Boolean(loc.canStaff ?? loc.can_staff),
        canSchedule: Boolean(loc.canSchedule ?? loc.can_schedule),
        canCreateJobs: Boolean(loc.canCreateJobs ?? loc.can_create_jobs),
        canCreateShifts: Boolean(loc.canCreateShifts ?? loc.can_create_shifts),
        canApproveTimesheets: Boolean(loc.canApproveTimesheets ?? loc.can_approve_timesheets),
      })),
    })),
    organizationUnits: data.organizationUnits || body.organizationUnits || [],
    scopedRoleAssignments: data.scopedRoleAssignments || body.scopedRoleAssignments || [],
    effectivePermissions: data.effectivePermissions || body.effectivePermissions || [],
    accessibleLocations: data.accessibleLocations || body.accessibleLocations || [],
    featureFlagsOrUiCapabilities: data.featureFlagsOrUiCapabilities || body.featureFlagsOrUiCapabilities || data.uiCapabilities || body.uiCapabilities,
    billingEntitlements: data.billingEntitlements || body.billingEntitlements,
    rateVisibility: data.rateVisibility || body.rateVisibility,
    lifecyclePermissions: data.lifecyclePermissions || body.lifecyclePermissions,
    workerOrgRelationships: data.workerOrgRelationships || body.workerOrgRelationships || [],
    ownStaffCountByOrgId: data.ownStaffCountByOrgId || body.ownStaffCountByOrgId || {},
    mobileCapabilities: data.mobileCapabilities || body.mobileCapabilities,
    setupState: data.setupState || body.setupState,
    onboardingStatus: data.onboardingStatus || body.onboardingStatus,
    onboardingCompletedAt: data.onboardingCompletedAt || body.onboardingCompletedAt,
    requiredSetupSteps: data.requiredSetupSteps || body.requiredSetupSteps || [],
    completedSetupSteps: data.completedSetupSteps || body.completedSetupSteps || [],
    currentRecommendedStep: data.currentRecommendedStep || body.currentRecommendedStep,
    onboarding: normalizeOnboardingState(data.onboarding || body.onboarding),
    industryType: data.industryType ?? body.industryType ?? data.tenant?.industry_type ?? null,
    industryPreset: data.industryPreset || body.industryPreset || null,
    terminology: data.terminology || body.terminology || data.industryPreset?.terminology || body.industryPreset?.terminology || {},
    dashboardPreset: data.dashboardPreset || body.dashboardPreset || data.industryPreset?.dashboardPreset || body.industryPreset?.dashboardPreset,
    availableIndustryTypes: data.availableIndustryTypes || body.availableIndustryTypes || [],
    isSystemSuperAdmin,
  } as AccessContext
}

export const updateAdminExperiencePreference = async (mode: 'simple' | 'advanced') => {
  const response = await api.patch('/api/admin/experience/preference', { mode })
  return response.data?.adminExperience
}

export const updateAdminExperienceSettings = async (settings: {
  defaultMode: 'simple' | 'advanced'
  guidedWorkflows: 'disabled' | 'available' | 'default_on' | 'required'
  allowAdvancedModeToggle: boolean
  rememberUserPreference: boolean
}) => {
  const response = await api.patch('/api/admin/experience/settings', settings)
  return response.data?.adminExperience
}

export type CompanyType =
  | 'construction'
  | 'staffing_agency'
  | 'security'
  | 'restaurant_franchise'
  | 'warehouse_logistics'
  | 'retail'
  | 'other'

export type OperatingModel =
  | 'company_only'
  | 'agency_only'
  | 'company_with_agencies'
  | 'subcontractor_network'

export interface OnboardingCompanyProfilePayload {
  businessProfileConfirmation?: import('./companyEnrichment').CompanyProfileConfirmation
  organizationName: string
  companyType: CompanyType
  operatingModel: OperatingModel
  workerCount?: number
  locationCount?: number
  supervisorCount?: number
  primaryOrgId?: string
}

export interface OnboardingLocationPayload {
  id?: string
  name: string
  address?: string
  city?: string
  region?: string
  country?: string
  timezone?: string
  defaultPunchRadius?: number
  latitude?: number
  longitude?: number
  ownerOrgId?: string
}

export interface OnboardingSupervisorPayload {
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  locationId?: string
  orgId?: string
  tenantWideAdmin?: boolean
}

export interface OnboardingWorkerPayload {
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  orgId?: string
  locationId?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value))

const optionalString = (value: unknown): string | null | undefined => {
  if (typeof value === 'string') return value
  if (value === null) return null
  return undefined
}

const normalizeOnboardingState = (payload: unknown): TenantOnboardingState | undefined => {
  const source = isRecord(payload) && isRecord(payload.onboarding) ? payload.onboarding : payload
  if (!isRecord(source)) return undefined
  const partial = source as Partial<TenantOnboardingState> & { updated_at?: string | null }
  return {
    ...partial,
    updatedAt: optionalString(partial.updatedAt) ?? optionalString(partial.updated_at) ?? null,
  } as TenantOnboardingState
}

const onboardingFrom = (payload: unknown): TenantOnboardingState => normalizeOnboardingState(payload) as TenantOnboardingState

export const getOnboardingStatus = async (): Promise<TenantOnboardingState> => {
  const response = await api.get('/api/onboarding/status')
  return onboardingFrom(response.data)
}

export const getOnboardingReadiness = async (): Promise<OnboardingReadiness> => {
  const response = await api.get('/api/onboarding/readiness')
  return response.data as OnboardingReadiness
}

export const startOnboarding = async (): Promise<TenantOnboardingState> => {
  const response = await api.post('/api/onboarding/start', {})
  return onboardingFrom(response.data)
}

export const updateOnboardingStatus = async (payload: {
  status?: TenantOnboardingState['status']
  completedSteps?: OnboardingStepKey[]
  currentStep?: OnboardingStepKey
  wizardPayload?: Record<string, unknown>
}): Promise<TenantOnboardingState> => {
  const response = await api.patch('/api/onboarding/status', payload)
  return onboardingFrom(response.data)
}

export const saveOnboardingCompanyProfile = async (payload: OnboardingCompanyProfilePayload) => {
  const response = await api.post('/api/onboarding/company-profile', payload)
  return response.data
}

export const saveOnboardingLocations = async (payload: { primaryOrgId?: string; locations: OnboardingLocationPayload[] }) => {
  const response = await api.post('/api/onboarding/locations', payload)
  return response.data
}

export const saveOnboardingGeofence = async (locationId: string, payload: { center: { lat: number; lon: number }; radiusMeters: number }) => {
  const response = await api.post(`/api/onboarding/locations/${locationId}/geofence`, payload)
  return response.data
}

export const saveOnboardingSupervisors = async (payload: { supervisors: OnboardingSupervisorPayload[] }) => {
  const response = await api.post('/api/onboarding/supervisors', payload)
  return response.data
}

export const previewOnboardingWorkerImport = async (payload: { rows?: Record<string, unknown>[]; csvText?: string }) => {
  const response = await api.post('/api/onboarding/workers/import-preview', payload)
  return response.data
}

export const saveOnboardingWorkers = async (payload: { workers: OnboardingWorkerPayload[] }) => {
  const response = await api.post('/api/onboarding/workers', payload)
  return response.data
}

export const saveOnboardingBillingPlan = async (payload: { planId?: string; planKey?: string; billingFrequency?: 'monthly' | 'annual' | 'manual' }) => {
  const response = await api.post('/api/onboarding/billing-plan', payload)
  return response.data
}

export const saveOnboardingFirstJob = async (payload: { skipped?: boolean; draft?: Record<string, unknown> }) => {
  const response = await api.post('/api/onboarding/first-job', payload)
  return response.data
}

export const completeOnboarding = async (): Promise<TenantOnboardingState> => {
  const response = await api.post('/api/onboarding/complete', {})
  return onboardingFrom(response.data)
}

export const skipOnboarding = async (): Promise<TenantOnboardingState> => {
  const response = await api.post('/api/onboarding/skip', {})
  return onboardingFrom(response.data)
}

export const listOrganizations = async (query: ListQuery = {}): Promise<Organization[]> => {
  const response = await api.get('/api/orgs', { params: query })
  return unwrapPaginated<Organization>(response.data, 'organizations').items
}

export const listOrganizationsPage = async (query: ListQuery = {}): Promise<PaginatedResponse<Organization>> => {
  const response = await api.get('/api/orgs', { params: query })
  return unwrapPaginated<Organization>(response.data, 'organizations')
}

export const createOrganization = async (payload: OrganizationPayload): Promise<Organization> => {
  const response = await api.post('/api/orgs', payload)
  return response.data?.organization as Organization
}

export const createAssociatedClientCompany = async (
  agencyOrgId: string,
  payload: AssociatedClientCompanyPayload,
): Promise<AssociatedClientCompanyResult> => {
  const response = await api.post(`/api/orgs/${agencyOrgId}/associated-client-companies`, payload)
  return response.data?.associatedClientCompany as AssociatedClientCompanyResult
}

export const updateOrganization = async (
  orgId: string,
  payload: OrganizationUpdatePayload,
): Promise<Organization> => {
  const response = await api.patch(`/api/orgs/${orgId}`, payload)
  return response.data?.organization as Organization
}

export type SystemTenantPayload = {
  name: string
  slug?: string
  status?: 'active' | 'suspended'
  billingEmail?: string
  industryType?: IndustryType | null
}

export const listSystemTenants = async (): Promise<AccessTenant[]> => {
  const response = await api.get('/api/system/tenants')
  return (response.data?.tenants || []) as AccessTenant[]
}

export const createSystemTenant = async (payload: SystemTenantPayload): Promise<AccessTenant> => {
  const response = await api.post('/api/system/tenants', payload)
  return response.data?.tenant as AccessTenant
}

export const updateSystemTenantIndustry = async (tenantId: string, industryType: IndustryType | null): Promise<AccessTenant> => {
  const response = await api.patch(`/api/system/tenants/${tenantId}/industry`, { industryType })
  return response.data?.tenant as AccessTenant
}

export type DeleteImpact = {
  canDelete: boolean
  recommendedAction: 'delete' | 'archive'
  dependencies: Array<{ type: string; count: number }>
}

export const getSystemOrganizationDeleteImpact = async (orgId: string): Promise<DeleteImpact> => {
  const response = await api.get(`/api/system/orgs/${orgId}/delete-impact`)
  return response.data as DeleteImpact
}

export const runSystemOrganizationLifecycleAction = async (
  orgId: string,
  action: 'suspend' | 'archive' | 'restore' | 'delete',
  reason?: string,
): Promise<Organization | null> => {
  const response = action === 'delete'
    ? await api.delete(`/api/system/orgs/${orgId}`, { data: { reason } })
    : await api.post(`/api/system/orgs/${orgId}/${action}`, { reason })
  return response.data?.organization || null
}

export const getSystemWorkerDeleteImpact = async (workerProfileId: string): Promise<DeleteImpact> => {
  const response = await api.get(`/api/system/workers/${workerProfileId}/delete-impact`)
  return response.data as DeleteImpact
}

export const runSystemWorkerLifecycleAction = async (
  workerProfileId: string,
  action: 'suspend' | 'archive' | 'restore' | 'delete' | 'anonymize',
  reason?: string,
) => {
  const response = action === 'delete'
    ? await api.delete(`/api/system/workers/${workerProfileId}`, { data: { reason } })
    : await api.post(`/api/system/workers/${workerProfileId}/${action}`, { reason })
  return response.data?.worker || null
}

export const getSystemUserDeleteImpact = async (userId: string): Promise<DeleteImpact> => {
  const response = await api.get(`/api/system/users/${userId}/delete-impact`)
  return response.data as DeleteImpact
}

export const runSystemUserLifecycleAction = async (
  userId: string,
  action: 'disable' | 'restore' | 'delete',
  reason?: string,
) => {
  const response = action === 'delete'
    ? await api.delete(`/api/system/users/${userId}`, { data: { reason } })
    : await api.post(`/api/system/users/${userId}/${action}`, { reason })
  return response.data?.user || null
}

const systemLifecycleRequest = async <T>(
  path: string,
  action: string,
  reason?: string,
  resultKey?: string,
  confirmation?: string,
): Promise<T | null> => {
  const payload = { reason, confirmation }
  const response = action === 'delete'
    ? await api.delete(path, { data: payload })
    : await api.post(`${path}/${action}`, payload)
  return (resultKey ? response.data?.[resultKey] : response.data) || null
}

export const getJobDeleteImpact = async (jobId: string): Promise<DeleteImpact> => (await api.get(`/api/jobs/${jobId}/delete-impact`)).data as DeleteImpact
export const archiveJob = (jobId: string, reason?: string) => systemLifecycleRequest<Job>(`/api/jobs/${jobId}`, 'archive', reason, 'job')
export const cancelJob = (jobId: string, reason?: string) => systemLifecycleRequest<Job>(`/api/jobs/${jobId}`, 'cancel', reason, 'job')
export const restoreJob = (jobId: string, reason?: string) => systemLifecycleRequest<Job>(`/api/jobs/${jobId}`, 'restore', reason, 'job')
export const deleteJob = (jobId: string, reason?: string) => systemLifecycleRequest<Job>(`/api/jobs/${jobId}`, 'delete', reason, 'job')
export const purgeDemoJob = (jobId: string, reason?: string) => systemLifecycleRequest<Job>(`/api/system/jobs/${jobId}`, 'purge-demo-data', reason, 'job', 'PURGE_DEMO_DATA')

export const getJobShiftDeleteImpact = async (shiftId: string): Promise<DeleteImpact> => (await api.get(`/api/system/job-shifts/${shiftId}/delete-impact`)).data as DeleteImpact
export const archiveJobShift = (shiftId: string, reason?: string) => systemLifecycleRequest<JobShift>(`/api/system/job-shifts/${shiftId}`, 'archive', reason, 'shift')
export const cancelSystemJobShift = (shiftId: string, reason?: string) => systemLifecycleRequest<JobShift>(`/api/system/job-shifts/${shiftId}`, 'cancel', reason, 'shift')
export const restoreJobShift = (shiftId: string, reason?: string) => systemLifecycleRequest<JobShift>(`/api/system/job-shifts/${shiftId}`, 'restore', reason, 'shift')
export const deleteJobShift = (shiftId: string, reason?: string) => systemLifecycleRequest<JobShift>(`/api/system/job-shifts/${shiftId}`, 'delete', reason, 'shift')
export const purgeDemoJobShift = (shiftId: string, reason?: string) => systemLifecycleRequest<JobShift>(`/api/system/job-shifts/${shiftId}`, 'purge-demo-data', reason, 'shift', 'PURGE_DEMO_DATA')

export const getJobAssignmentDeleteImpact = async (assignmentId: string): Promise<DeleteImpact> => (await api.get(`/api/system/job-assignments/${assignmentId}/delete-impact`)).data as DeleteImpact
export const cancelJobAssignment = (assignmentId: string, reason?: string) => systemLifecycleRequest<JobAssignment>(`/api/system/job-assignments/${assignmentId}`, 'cancel', reason, 'assignment')
export const revokeJobAssignment = (assignmentId: string, reason?: string) => systemLifecycleRequest<JobAssignment>(`/api/system/job-assignments/${assignmentId}`, 'revoke', reason, 'assignment')
export const restoreJobAssignment = (assignmentId: string, reason?: string) => systemLifecycleRequest<JobAssignment>(`/api/system/job-assignments/${assignmentId}`, 'restore', reason, 'assignment')
export const deleteJobAssignment = (assignmentId: string, reason?: string) => systemLifecycleRequest<JobAssignment>(`/api/system/job-assignments/${assignmentId}`, 'delete', reason, 'assignment')
export const purgeDemoJobAssignment = (assignmentId: string, reason?: string) => systemLifecycleRequest<JobAssignment>(`/api/system/job-assignments/${assignmentId}`, 'purge-demo-data', reason, 'assignment', 'PURGE_DEMO_DATA')

export const getLocationDeleteImpact = async (locationId: string): Promise<DeleteImpact> => (await api.get(`/api/system/locations/${locationId}/delete-impact`)).data as DeleteImpact
export const disableLocation = (locationId: string, reason?: string) => systemLifecycleRequest<AccessibleLocation>(`/api/system/locations/${locationId}`, 'disable', reason, 'location')
export const archiveLocation = (locationId: string, reason?: string) => systemLifecycleRequest<AccessibleLocation>(`/api/system/locations/${locationId}`, 'archive', reason, 'location')
export const restoreLocation = (locationId: string, reason?: string) => systemLifecycleRequest<AccessibleLocation>(`/api/system/locations/${locationId}`, 'restore', reason, 'location')
export const deleteLocation = (locationId: string, reason?: string) => systemLifecycleRequest<AccessibleLocation>(`/api/system/locations/${locationId}`, 'delete', reason, 'location')
export const purgeDemoLocation = (locationId: string, reason?: string) => systemLifecycleRequest<AccessibleLocation>(`/api/system/locations/${locationId}`, 'purge-demo-data', reason, 'location', 'PURGE_DEMO_DATA')

export const getLocationGeofenceDeleteImpact = async (locationId: string): Promise<DeleteImpact> => (await api.get(`/api/system/locations/${locationId}/geofence/delete-impact`)).data as DeleteImpact
export const disableLocationGeofence = (locationId: string, reason?: string) => systemLifecycleRequest<unknown>(`/api/system/locations/${locationId}/geofence`, 'disable', reason, 'location')
export const clearLocationGeofence = (locationId: string, reason?: string) => systemLifecycleRequest<unknown>(`/api/system/locations/${locationId}/geofence`, 'clear', reason, 'location')
export const restoreLocationGeofence = (locationId: string, reason?: string) => systemLifecycleRequest<unknown>(`/api/system/locations/${locationId}/geofence`, 'restore', reason, 'location')

export const listOrganizationRelationships = async (orgId: string): Promise<OrganizationRelationship[]> => {
  const response = await api.get(`/api/orgs/${orgId}/relationships`)
  return (response.data?.relationships || []) as OrganizationRelationship[]
}

export const createOrganizationRelationship = async (
  orgId: string,
  payload: OrganizationRelationshipPayload,
): Promise<OrganizationRelationship> => {
  const response = await api.post(`/api/orgs/${orgId}/relationships`, payload)
  return response.data?.relationship as OrganizationRelationship
}

export const updateOrganizationRelationship = async (
  relationshipId: string,
  payload: OrganizationRelationshipUpdatePayload,
): Promise<OrganizationRelationship> => {
  const response = await api.patch(`/api/org-relationships/${relationshipId}`, payload)
  return response.data?.relationship as OrganizationRelationship
}

export const updateOrganizationRelationshipPolicy = async (
  relationshipId: string,
  payload: OrganizationRelationshipPolicyPayload,
): Promise<OrganizationRelationship> => {
  const response = await api.patch(`/api/org-relationships/${relationshipId}/policy`, payload)
  return response.data?.relationship as OrganizationRelationship
}

export const listGrantedLocationsForRelationship = async (relationshipId: string): Promise<import('../types/access').GrantedClientLocation[]> => {
  const response = await api.get(`/api/org-relationships/${relationshipId}/granted-locations`)
  return (response.data?.grantedLocations || []).map((loc: any) => ({
    id: loc.id || loc.location_id,
    locationId: loc.location_id || loc.id,
    locationTenantId: loc.location_tenant_id || loc.locationTenantId || '',
    locationKey: loc.location_key || loc.locationKey || null,
    name: loc.location_name || loc.name || '',
    companyTenantId: loc.company_tenant_id || loc.companyTenantId || null,
    companyOrgId: loc.company_org_id || loc.companyOrgId || null,
    agencyTenantId: loc.agency_tenant_id || loc.agencyTenantId || null,
    agencyOrgId: loc.agency_org_id || loc.agencyOrgId || null,
    ownerOrgId: loc.owner_org_id || loc.ownerOrgId || null,
    canView: Boolean(loc.can_view ?? loc.canView ?? true),
    canStaff: Boolean(loc.can_staff ?? loc.canStaff),
    canSchedule: Boolean(loc.can_schedule ?? loc.canSchedule),
    canCreateJobs: Boolean(loc.can_create_jobs ?? loc.canCreateJobs),
    canCreateShifts: Boolean(loc.can_create_shifts ?? loc.canCreateShifts),
    canApproveTimesheets: Boolean(loc.can_approve_timesheets ?? loc.canApproveTimesheets),
  }))
}

export const searchOrganizationsForRelationship = async (
  type: Extract<OrganizationType, 'agency' | 'company'>,
  q: string,
): Promise<Organization[]> => {
  const response = await api.get('/api/orgs/search', { params: { type, q } })
  return (response.data?.organizations || []) as Organization[]
}

export const createOrganizationRelationshipRequest = async (
  payload: { sourceOrgId?: string; targetOrgId: string },
): Promise<OrganizationRelationship> => {
  const response = await api.post('/api/org-relationship-requests', payload)
  return (response.data?.relationship || response.data?.request) as OrganizationRelationship
}

export const listIncomingOrganizationRelationshipRequests = async (): Promise<OrganizationRelationship[]> => {
  const response = await api.get('/api/org-relationship-requests/incoming')
  return (response.data?.requests || []) as OrganizationRelationship[]
}

export const listOutgoingOrganizationRelationshipRequests = async (): Promise<OrganizationRelationship[]> => {
  const response = await api.get('/api/org-relationship-requests/outgoing')
  return (response.data?.requests || []) as OrganizationRelationship[]
}

export const acceptOrganizationRelationshipRequest = async (requestId: string): Promise<OrganizationRelationship> => {
  const response = await api.post(`/api/org-relationship-requests/${requestId}/accept`, {})
  return (response.data?.relationship || response.data?.request) as OrganizationRelationship
}

export const rejectOrganizationRelationshipRequest = async (requestId: string): Promise<OrganizationRelationship> => {
  const response = await api.post(`/api/org-relationship-requests/${requestId}/reject`, {})
  return (response.data?.relationship || response.data?.request) as OrganizationRelationship
}

export const createOrganizationConnectionInvite = async (
  orgId: string,
  payload: { targetOrganizationType: 'agency' | 'company'; expiresInDays?: number; autoAccept?: boolean },
): Promise<OrganizationConnectionInvite> => {
  const response = await api.post(`/api/orgs/${orgId}/connection-invites`, payload)
  return response.data?.invite as OrganizationConnectionInvite
}

export const listOrganizationConnectionInvites = async (orgId: string): Promise<OrganizationConnectionInvite[]> => {
  const response = await api.get(`/api/orgs/${orgId}/connection-invites`)
  return (response.data?.invites || []) as OrganizationConnectionInvite[]
}

export const redeemOrganizationConnectionInvite = async (
  payload: { code: string; redeemerOrganizationId: string },
): Promise<OrganizationRelationship> => {
  const response = await api.post('/api/org-connection-invites/redeem', payload)
  return (response.data?.relationship || response.data?.request) as OrganizationRelationship
}

export const revokeOrganizationConnectionInvite = async (inviteId: string): Promise<OrganizationConnectionInvite | null> => {
  const response = await api.post(`/api/org-connection-invites/${inviteId}/revoke`, {})
  return (response.data?.invite || null) as OrganizationConnectionInvite | null
}

export const listSystemUsers = async (
  params: { q?: string; tenantId?: string; limit?: number; offset?: number } = {},
): Promise<GlobalUserDirectoryResult> => {
  const response = await api.get('/api/system/users', { params })
  return {
    users: (response.data?.users || []) as GlobalUserDirectoryEntry[],
    total: Number(response.data?.total ?? 0),
    limit: Number(response.data?.limit ?? 50),
    offset: Number(response.data?.offset ?? 0),
  }
}

export const searchSystemOrganizations = async (
  params: { type?: 'agency' | 'company'; q?: string; tenantId?: string },
): Promise<Organization[]> => {
  const response = await api.get('/api/system/organizations', { params })
  return (response.data?.organizations || []) as Organization[]
}

export const createSystemOrganizationRelationship = async (payload: {
  sourceOrganizationId: string
  sourceTenantId: string
  targetOrganizationId: string
  targetTenantId: string
  relationshipType: 'agency_company' | 'company_agency'
  status?: 'pending' | 'active'
}): Promise<OrganizationRelationship> => {
  const response = await api.post('/api/system/organization-relationships', payload)
  return response.data?.relationship as OrganizationRelationship
}

export const listOrganizationUnits = async (orgId: string): Promise<OrganizationUnit[]> => {
  const response = await api.get(`/api/orgs/${orgId}/units`)
  return (response.data?.units || []) as OrganizationUnit[]
}

export const createOrganizationUnit = async (
  orgId: string,
  payload: OrganizationUnitPayload,
): Promise<OrganizationUnit> => {
  const response = await api.post(`/api/orgs/${orgId}/units`, payload)
  return response.data?.unit as OrganizationUnit
}

export const updateOrganizationUnit = async (
  ouId: string,
  payload: OrganizationUnitUpdatePayload,
): Promise<OrganizationUnit> => {
  const response = await api.patch(`/api/org-units/${ouId}`, payload)
  return response.data?.unit as OrganizationUnit
}

export const getUnreadNotificationCount = async (): Promise<{ count: number; messageUnreadCount: number }> => {
  const response = await api.get('/api/notifications/unread-count')
  return {
    count: Number(response.data?.count || 0),
    messageUnreadCount: Number(response.data?.messageUnreadCount || 0),
  }
}

export const getWhatsappNotificationStatus = async (): Promise<WhatsappNotificationStatus> => {
  const response = await api.get('/api/admin/whatsapp/status', { headers: { 'x-role': 'corporate' } })
  return response.data?.status as WhatsappNotificationStatus
}

export const updateWhatsappProviderSettings = async (payload: WhatsappProviderSettingsPayload): Promise<WhatsappNotificationStatus> => {
  const response = await api.patch('/api/admin/whatsapp/settings', payload, { headers: { 'x-role': 'corporate' } })
  return response.data?.status as WhatsappNotificationStatus
}

export interface AutoPunchSettings {
  auto_punch_enabled: boolean
  auto_punch_in_after_minutes: number
  auto_punch_out_after_minutes: number
  require_scheduled_shift: boolean
  auto_punch_out_mode: 'automatic' | 'supervisor_review'
}

export interface AutoPunchSettingsPayload {
  orgId?: string | null
  locationId?: string | null
  autoPunchEnabled: boolean
  autoPunchInAfterMinutes: number
  autoPunchOutAfterMinutes: number
  requireScheduledShift: boolean
  autoPunchOutMode: 'automatic' | 'supervisor_review'
}

export const getAutoPunchSettings = async (orgId?: string, locationId?: string): Promise<AutoPunchSettings> => {
  const params = new URLSearchParams()
  if (orgId) params.set('orgId', orgId)
  if (locationId) params.set('locationId', locationId)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const response = await api.get(`/api/admin/auto-punch/settings${qs}`, { headers: { 'x-role': 'corporate' } })
  return response.data?.settings as AutoPunchSettings
}

export const updateAutoPunchSettings = async (payload: AutoPunchSettingsPayload): Promise<AutoPunchSettings> => {
  const response = await api.patch('/api/admin/auto-punch/settings', payload, { headers: { 'x-role': 'corporate' } })
  return response.data?.settings as AutoPunchSettings
}

export const archiveOrgUnit = async (ouId: string, reason?: string): Promise<OrganizationUnit> => {
  const response = await api.patch(`/api/org-units/${ouId}/archive`, reason ? { reason } : {})
  return (response.data?.data || response.data?.unit) as OrganizationUnit
}

export const restoreOrgUnit = async (ouId: string): Promise<OrganizationUnit> => {
  const response = await api.patch(`/api/org-units/${ouId}/restore`, {})
  return (response.data?.data || response.data?.unit) as OrganizationUnit
}

export const getOrganizationBranding = async (orgId: string): Promise<OrganizationBranding> => {
  const response = await api.get(`/api/orgs/${orgId}/branding`)
  return response.data?.branding as OrganizationBranding
}

export const updateOrganizationBranding = async (
  orgId: string,
  payload: OrganizationBrandingPayload,
): Promise<OrganizationBranding> => {
  const response = await api.patch(`/api/orgs/${orgId}/branding`, payload)
  return response.data?.branding as OrganizationBranding
}

export const uploadOrganizationBrandingAsset = async (
  orgId: string,
  assetType: 'logo' | 'banner',
  payload: { url?: string | null; fileId?: string | null; fileName?: string | null; mimeType: string; sizeBytes: number } | File,
): Promise<OrganizationBranding> => {
  if (typeof File !== 'undefined' && payload instanceof File) {
    const response = await api.post(`/api/orgs/${orgId}/branding/${assetType}/file`, payload, {
      headers: {
        'Content-Type': payload.type || 'application/octet-stream',
        'X-File-Name': payload.name,
      },
    })
    return response.data?.branding as OrganizationBranding
  }
  const response = await api.post(`/api/orgs/${orgId}/branding/${assetType}`, payload)
  return response.data?.branding as OrganizationBranding
}

export const deleteOrganizationBrandingAsset = async (
  orgId: string,
  assetType: 'logo' | 'banner',
): Promise<OrganizationBranding> => {
  const response = await api.delete(`/api/orgs/${orgId}/branding/${assetType}`)
  return response.data?.branding as OrganizationBranding
}

export const createScopedRoleAssignment = async (
  payload: ScopedRoleAssignmentPayload,
): Promise<ScopedRoleAssignment> => {
  const response = await api.post('/api/scoped-role-assignments', payload)
  return response.data?.assignment as ScopedRoleAssignment
}

export const updateScopedRoleAssignment = async (
  assignmentId: string,
  payload: ScopedRoleAssignmentUpdatePayload,
): Promise<ScopedRoleAssignment> => {
  const response = await api.patch(`/api/scoped-role-assignments/${assignmentId}`, payload)
  return response.data?.assignment as ScopedRoleAssignment
}

export const disableScopedRoleAssignment = async (assignmentId: string): Promise<ScopedRoleAssignment> => {
  const response = await api.delete(`/api/scoped-role-assignments/${assignmentId}`)
  return response.data?.assignment as ScopedRoleAssignment
}

export const listOrganizationUsers = async (orgId: string, query: ListQuery = {}): Promise<OrganizationUserSummary[]> => {
  const response = await api.get(`/api/orgs/${orgId}/users`, { params: query })
  return unwrapPaginated<OrganizationUserSummary>(response.data, 'users').items
}

export const listOrganizationUsersPage = async (orgId: string, query: ListQuery = {}, signal?: AbortSignal): Promise<PaginatedResponse<OrganizationUserSummary>> => {
  const response = await api.get(`/api/orgs/${orgId}/users`, { params: query, signal })
  return unwrapPaginated<OrganizationUserSummary>(response.data, 'users')
}

export const listOrganizationInvites = async (orgId: string): Promise<OrganizationInviteSummary[]> => {
  const response = await api.get(`/api/orgs/${orgId}/invites`)
  return (response.data?.invites || []) as OrganizationInviteSummary[]
}

export const createOrganizationUser = async (
  orgId: string,
  payload: OrganizationUserInvitePayload,
): Promise<OrganizationUserSummary> => {
  const response = await api.post(`/api/orgs/${orgId}/users`, payload)
  return response.data?.user as OrganizationUserSummary
}

export const listOrganizationRoles = async (orgId: string): Promise<TenantRoleSummary[]> => {
  const response = await api.get(`/api/orgs/${orgId}/roles`)
  return (response.data?.roles || []) as TenantRoleSummary[]
}

export const listAdminCapabilities = async (): Promise<AdminCapabilitySummary[]> => {
  const response = await api.get('/api/admin/capabilities')
  return (response.data?.capabilities || []) as AdminCapabilitySummary[]
}

export const createTenantRole = async (
  payload: { roleKey?: string; label: string; description?: string | null; permissionKeys: string[] },
): Promise<TenantRoleSummary> => {
  const response = await api.post('/api/tenant-roles', payload)
  return response.data?.role as TenantRoleSummary
}

export const updateTenantRole = async (
  roleId: string,
  payload: { label?: string; description?: string | null; permissionKeys?: string[] },
): Promise<TenantRoleSummary> => {
  const response = await api.patch(`/api/tenant-roles/${roleId}`, payload)
  return response.data?.role as TenantRoleSummary
}

export const deleteTenantRole = async (roleId: string): Promise<TenantRoleSummary> => {
  const response = await api.delete(`/api/tenant-roles/${roleId}`)
  return response.data?.role as TenantRoleSummary
}

export const inviteOrganizationUser = async (
  orgId: string,
  payload: OrganizationUserInvitePayload,
): Promise<OrganizationUserSummary & { invite?: unknown; token?: string }> => {
  const response = await api.post(`/api/orgs/${orgId}/invites`, payload)
  return (response.data?.user || { invite: response.data?.invite, token: response.data?.token }) as OrganizationUserSummary & { invite?: unknown; token?: string }
}

export const updateOrganizationUser = async (
  orgId: string,
  userId: string,
  payload: OrganizationUserUpdatePayload,
): Promise<OrganizationUserSummary> => {
  const response = await api.patch(`/api/orgs/${orgId}/users/${userId}`, payload)
  return response.data?.user as OrganizationUserSummary
}

export const disableOrganizationUser = async (orgId: string, userId: string) => {
  const response = await api.delete(`/api/orgs/${orgId}/users/${userId}`)
  return response.data?.membership
}

export const resetOrganizationUserPassword = async (
  orgId: string,
  userId: string,
  password: string,
): Promise<OrganizationUserSummary> => {
  const response = await api.post(`/api/orgs/${orgId}/users/${userId}/reset-password`, { password })
  return response.data?.user as OrganizationUserSummary
}

export const revokeOrganizationInvite = async (inviteId: string): Promise<OrganizationInviteSummary> => {
  const response = await api.patch(`/api/org-invites/${inviteId}/revoke`, {})
  return response.data?.invite as OrganizationInviteSummary
}

export const listOrganizationUserAccess = async (
  orgId: string,
  userId: string,
): Promise<ScopedRoleAssignment[]> => {
  const response = await api.get(`/api/orgs/${orgId}/users/${userId}/access`)
  return (response.data?.assignments || []) as ScopedRoleAssignment[]
}

export const assignOrganizationUserRole = async (
  orgId: string,
  userId: string,
  payload: OrganizationUserRolePayload,
): Promise<ScopedRoleAssignment> => {
  const response = await api.post(`/api/orgs/${orgId}/users/${userId}/roles`, payload)
  return response.data?.assignment as ScopedRoleAssignment
}

export const revokeOrganizationUserRole = async (
  orgId: string,
  userId: string,
  assignmentId: string,
): Promise<ScopedRoleAssignment> => {
  const response = await api.delete(`/api/orgs/${orgId}/users/${userId}/roles/${assignmentId}`)
  return response.data?.assignment as ScopedRoleAssignment
}

export const listOrgWorkers = async (orgId: string): Promise<WorkerOrgRelationship[]> => {
  const response = await api.get(`/api/orgs/${orgId}/workers`)
  return (response.data?.relationships || response.data?.workers || []) as WorkerOrgRelationship[]
}

export const createWorkerRelationship = async (
  orgId: string,
  payload: WorkerRelationshipPayload,
): Promise<WorkerOrgRelationship> => {
  const response = await api.post(`/api/orgs/${orgId}/workers`, payload)
  return response.data?.relationship as WorkerOrgRelationship
}

export const inviteOrgWorker = async (
  orgId: string,
  payload: WorkerInvitePayload,
): Promise<WorkerOrgRelationship> => {
  const response = await api.post(`/api/orgs/${orgId}/workers/invite`, payload)
  return response.data?.relationship as WorkerOrgRelationship
}

export interface WorkerInviteCode {
  id: string
  code?: string
  organizationId: string
  organizationName: string
  relationshipType: string
  email?: string | null
  status: 'active' | 'revoked' | 'expired'
  expiresAt: string
  createdAt: string
  useCount?: number
  maxUses?: number | null
  createdByEmail?: string | null
}

export const createWorkerInviteCode = async (payload: {
  organizationId: string
  relationshipType?: string
  email?: string | null
  expiryDays?: number
}): Promise<WorkerInviteCode> => {
  const response = await api.post('/api/admin/workers/invite-codes', payload)
  return response.data?.inviteCode as WorkerInviteCode
}

export const listWorkerInviteCodes = async (params: {
  orgId?: string
  status?: 'active' | 'revoked' | 'expired'
} = {}): Promise<WorkerInviteCode[]> => {
  const response = await api.get('/api/admin/workers/invite-codes', { params })
  return (response.data?.inviteCodes || []) as WorkerInviteCode[]
}

export const revokeWorkerInviteCode = async (inviteCodeId: string): Promise<WorkerInviteCode> => {
  const response = await api.patch(`/api/admin/workers/invite-codes/${inviteCodeId}/revoke`, {})
  return response.data?.inviteCode as WorkerInviteCode
}

export const associateOrgWorker = async (
  orgId: string,
  workerProfileId: string,
  payload: Partial<WorkerRelationshipPayload> = {},
): Promise<WorkerOrgRelationship> => {
  const response = await api.post(`/api/orgs/${orgId}/workers/${workerProfileId}/associate`, payload)
  return response.data?.relationship as WorkerOrgRelationship
}

export const shareOrgWorker = async (
  orgId: string,
  workerProfileId: string,
  payload: { targetOrgId?: string; status?: WorkerRelationshipPayload['status']; exclusiveUntil?: string | null } = {},
): Promise<WorkerOrgRelationship> => {
  const response = await api.post(`/api/orgs/${orgId}/workers/${workerProfileId}/share`, payload)
  return response.data?.relationship as WorkerOrgRelationship
}

export const removeOrgWorker = async (orgId: string, workerProfileId: string): Promise<WorkerOrgRelationship> => {
  const response = await api.delete(`/api/orgs/${orgId}/workers/${workerProfileId}`)
  return response.data?.relationship as WorkerOrgRelationship
}

export const updateWorkerRelationship = async (
  relationshipId: string,
  payload: WorkerRelationshipUpdatePayload,
): Promise<WorkerOrgRelationship> => {
  const response = await api.patch(`/api/worker-org-relationships/${relationshipId}`, payload)
  return response.data?.relationship as WorkerOrgRelationship
}

export const endWorkerRelationship = async (relationshipId: string): Promise<WorkerOrgRelationship> => {
  const response = await api.post(`/api/worker-org-relationships/${relationshipId}/end`)
  return response.data?.relationship as WorkerOrgRelationship
}

export const createWorkerOrgRelationship = createWorkerRelationship
export const updateWorkerOrgRelationship = updateWorkerRelationship
export const endWorkerOrgRelationship = endWorkerRelationship

export const listFreeAgentWorkers = async (orgId?: string): Promise<FreeAgentWorkerProfile[]> => {
  const response = await api.get('/api/workers/free-agents', { params: orgId ? { orgId } : undefined })
  return (response.data?.freeAgents || []) as FreeAgentWorkerProfile[]
}

export const listFreeAgents = listFreeAgentWorkers

export const updateWorkerProfileVisibility = async (
  workerProfileId: string,
  payload: WorkerProfileVisibilityPayload,
) => {
  const response = await api.patch(`/api/worker-profiles/${workerProfileId}/visibility`, payload)
  return response.data?.profile
}

export const listWorkerProfiles = async (filters: ListParams = {}): Promise<WorkerProfileDetail[]> => {
  const response = await api.get('/api/worker-profiles', { params: filters })
  return unwrapPaginated<WorkerProfileDetail>(response.data, 'workers').items
}

export const listWorkerProfilesPage = async (filters: ListParams = {}, signal?: AbortSignal): Promise<PaginatedResponse<WorkerProfileDetail>> => {
  const response = await api.get('/api/worker-profiles', { params: filters, signal })
  return unwrapPaginated<WorkerProfileDetail>(response.data, 'workers')
}

export const getWorkerProfile = async (workerProfileId: string): Promise<WorkerProfileDetail> => {
  const response = await api.get(`/api/worker-profiles/${workerProfileId}`)
  return response.data?.worker as WorkerProfileDetail
}

export const getWorkerReliability = async (workerProfileId: string): Promise<WorkerReliabilityProfile | null> => {
  const response = await api.get(`/api/admin/workers/${workerProfileId}/reliability`)
  return normalizeReliability(response.data?.reliability)
}

export const getWorkerAvailabilityForAdmin = async (workerProfileId: string): Promise<WorkerAvailabilitySummary> => {
  const response = await api.get(`/api/admin/workers/${workerProfileId}/availability`)
  const body = response.data || {}
  return {
    recurringAvailability: body.recurringAvailability || body.recurring_availability || [],
    unavailabilityWindows: body.unavailabilityWindows || body.unavailability_windows || [],
    upcomingConflicts: body.upcomingConflicts || body.upcoming_conflicts || [],
    profileAvailability: body.profileAvailability || body.profile_availability || {},
  }
}

export const listWorkerAttendanceEventsForAdmin = async (workerProfileId: string): Promise<WorkerAttendanceEvent[]> => {
  const response = await api.get(`/api/admin/workers/${workerProfileId}/attendance-events`)
  return (response.data?.events || []) as WorkerAttendanceEvent[]
}

export const listWorkerReviewsForAdmin = async (workerProfileId: string): Promise<WorkerSupervisorReview[]> => {
  const response = await api.get(`/api/admin/workers/${workerProfileId}/reviews`)
  return (response.data?.reviews || []) as WorkerSupervisorReview[]
}

export const listMyAvailability = async (): Promise<WorkerAvailabilityWindow[]> => {
  const response = await api.get('/api/workers/me/availability')
  return (response.data?.availability || []) as WorkerAvailabilityWindow[]
}

export const createMyAvailability = async (payload: Record<string, unknown>): Promise<WorkerAvailabilityWindow> => {
  const response = await api.post('/api/workers/me/availability', payload)
  return response.data?.availability as WorkerAvailabilityWindow
}

export const updateMyAvailability = async (availabilityId: string, payload: Record<string, unknown>): Promise<WorkerAvailabilityWindow> => {
  const response = await api.patch(`/api/workers/me/availability/${availabilityId}`, payload)
  return response.data?.availability as WorkerAvailabilityWindow
}

export const deleteMyAvailability = async (availabilityId: string): Promise<WorkerAvailabilityWindow> => {
  const response = await api.delete(`/api/workers/me/availability/${availabilityId}`)
  return response.data?.availability as WorkerAvailabilityWindow
}

export const listMyUnavailability = async (): Promise<WorkerUnavailabilityWindow[]> => {
  const response = await api.get('/api/workers/me/unavailability')
  return (response.data?.unavailability || []) as WorkerUnavailabilityWindow[]
}

export const createMyUnavailability = async (payload: Record<string, unknown>): Promise<WorkerUnavailabilityWindow> => {
  const response = await api.post('/api/workers/me/unavailability', payload)
  return response.data?.unavailability as WorkerUnavailabilityWindow
}

export const deleteMyUnavailability = async (unavailabilityId: string): Promise<WorkerUnavailabilityWindow> => {
  const response = await api.delete(`/api/workers/me/unavailability/${unavailabilityId}`)
  return response.data?.unavailability as WorkerUnavailabilityWindow
}

export const listMyOpenShifts = async (filters: Record<string, string | undefined> = {}): Promise<JobShift[]> => {
  const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value)))
  const response = await api.get('/api/workers/me/open-shifts', { params })
  return (response.data?.shifts || []) as JobShift[]
}

export const listRecommendedOpenShifts = async (): Promise<JobShift[]> => {
  const response = await api.get('/api/workers/me/open-shifts/recommended')
  return (response.data?.shifts || []) as JobShift[]
}

export const applyToOpenShift = async (shiftId: string, payload: { coverNote?: string | null } = {}): Promise<ShiftApplication> => {
  const response = await api.post(`/api/workers/me/open-shifts/${shiftId}/apply`, payload)
  return response.data?.application as ShiftApplication
}

export const listMyShiftApplications = async (): Promise<ShiftApplication[]> => {
  const response = await api.get('/api/workers/me/applications')
  return (response.data?.applications || []) as ShiftApplication[]
}

export const withdrawMyShiftApplication = async (applicationId: string): Promise<ShiftApplication> => {
  const response = await api.post(`/api/workers/me/shift-applications/${applicationId}/withdraw`)
  return response.data?.application as ShiftApplication
}

export const updateWorkerProfile = async (
  workerProfileId: string,
  payload: {
    firstName?: string | null
    lastName?: string | null
    phone?: string | null
    publicHeadline?: string | null
    bio?: string | null
    address?: Record<string, unknown> | null
    emergencyContact?: Record<string, unknown> | null
    languages?: string[] | null
    availability?: Record<string, unknown> | null
    preferredLocationIds?: string[] | null
    workEligibility?: Record<string, unknown> | null
    notes?: string | null
    profilePhotoUrl?: string | null
    avatarUrl?: string | null
    metadata?: Record<string, unknown> | null
    addressLine1?: string | null
    addressLine2?: string | null
    city?: string | null
    region?: string | null
    postalCode?: string | null
    country?: string | null
    homeLat?: number | null
    homeLon?: number | null
    workSearchLat?: number | null
    workSearchLon?: number | null
    workSearchRadiusMeters?: number | null
    workRadiusKm?: number | null
    locationConsent?: boolean | null
    preferredWorkAreas?: Array<Record<string, unknown>> | null
  },
): Promise<WorkerProfileDetail> => {
  const response = await api.patch(`/api/worker-profiles/${workerProfileId}`, payload)
  return response.data?.worker as WorkerProfileDetail
}

export const listWorkerSkills = async (workerProfileId: string): Promise<WorkerSkill[]> => {
  const response = await api.get(`/api/workers/${workerProfileId}/skills`)
  return (response.data?.workerSkills || []) as WorkerSkill[]
}

export const upsertWorkerSkill = async (
  workerProfileId: string,
  skillId: string,
  payload: { proficiencyLevel?: string | null; verifiedByOrgId?: string | null; verifiedAt?: string | null } = {},
): Promise<WorkerSkill> => {
  const response = await api.put(`/api/workers/${workerProfileId}/skills/${skillId}`, payload)
  return response.data?.workerSkill as WorkerSkill
}

export const deleteWorkerSkill = async (workerProfileId: string, skillId: string): Promise<WorkerSkill> => {
  const response = await api.delete(`/api/workers/${workerProfileId}/skills/${skillId}`)
  return response.data?.workerSkill as WorkerSkill
}

export const listSharingAgreements = async (orgId: string, query: ListQuery = {}): Promise<SharingAgreement[]> => {
  const response = await api.get(`/api/orgs/${orgId}/sharing-agreements`, { params: query })
  return unwrapPaginated<SharingAgreement>(response.data, 'agreements').items
}

export const listSharingAgreementsPage = async (orgId: string, query: ListQuery = {}): Promise<PaginatedResponse<SharingAgreement>> => {
  const response = await api.get(`/api/orgs/${orgId}/sharing-agreements`, { params: query })
  return unwrapPaginated<SharingAgreement>(response.data, 'agreements')
}

export const listSharingAgreementsForOrg = listSharingAgreements

export const createSharingAgreement = async (
  orgId: string,
  payload: SharingAgreementPayload,
): Promise<SharingAgreement> => {
  const response = await api.post(`/api/orgs/${orgId}/sharing-agreements`, payload)
  return response.data?.agreement as SharingAgreement
}

export const getSharingAgreement = async (
  agreementId: string,
): Promise<{ agreement: SharingAgreement; resources: SharingAgreementResource[] }> => {
  const response = await api.get(`/api/sharing-agreements/${agreementId}`)
  return {
    agreement: response.data?.agreement as SharingAgreement,
    resources: (response.data?.resources || []) as SharingAgreementResource[],
  }
}

export const updateSharingAgreement = async (
  agreementId: string,
  payload: SharingAgreementUpdatePayload,
): Promise<SharingAgreement> => {
  const response = await api.patch(`/api/sharing-agreements/${agreementId}`, payload)
  return response.data?.agreement as SharingAgreement
}

export const addSharingAgreementResource = async (
  agreementId: string,
  payload: SharingAgreementResourcePayload,
): Promise<SharingAgreementResource> => {
  const response = await api.post(`/api/sharing-agreements/${agreementId}/resources`, payload)
  return response.data?.resource as SharingAgreementResource
}

export const removeSharingAgreementResource = async (
  agreementId: string,
  resourceId: string,
): Promise<SharingAgreementResource> => {
  const response = await api.delete(`/api/sharing-agreements/${agreementId}/resources/${resourceId}`)
  return response.data?.resource as SharingAgreementResource
}

export const listWorkerDocumentConsents = async (orgId: string): Promise<WorkerDocumentConsent[]> => {
  const response = await api.get(`/api/orgs/${orgId}/worker-document-consents`)
  return ordinaryDocumentConsents((response.data?.consents || []) as WorkerDocumentConsent[])
}

export const listWorkerDocumentConsentsForWorker = async (
  workerProfileId: string,
  orgId: string,
): Promise<WorkerDocumentConsent[]> => {
  const response = await api.get(`/api/worker-profiles/${workerProfileId}/document-consents`, { params: { orgId } })
  return ordinaryDocumentConsents((response.data?.consents || []) as WorkerDocumentConsent[])
}

export const createWorkerDocumentConsent = async (
  workerProfileId: string,
  payload: WorkerDocumentConsentPayload,
): Promise<WorkerDocumentConsent> => {
  const response = await api.post(`/api/worker-profiles/${workerProfileId}/document-consents`, payload)
  return response.data?.consent as WorkerDocumentConsent
}

export const updateWorkerDocumentConsent = async (
  consentId: string,
  payload: WorkerDocumentConsentUpdatePayload,
): Promise<WorkerDocumentConsent> => {
  const response = await api.patch(`/api/worker-document-consents/${consentId}`, payload)
  return response.data?.consent as WorkerDocumentConsent
}

export const listWorkerDocumentsForManager = async (workerProfileId: string): Promise<WorkerProfileDocument[]> => {
  const response = await api.get(`/api/admin/workers/${workerProfileId}/documents`)
  return (response.data?.data || response.data?.documents || []) as WorkerProfileDocument[]
}

export const listWorkerDocumentRisks = async (expiringWithinDays = 30, tenantId?: string, signal?: AbortSignal) => {
  const response = await api.get('/api/admin/document-risks', { params: { expiringWithinDays }, signal, ...(tenantId ? { headers: { 'x-tenant-id': tenantId } } : {}) })
  if (response.data?.success !== true || !Array.isArray(response.data.risks)) throw new Error('Invalid document risk response')
  return response.data.risks as WorkerDocumentRisk[]
}

export const createWorkerDocumentForManager = async (
  workerProfileId: string,
  payload: Partial<WorkerProfileDocument>,
): Promise<WorkerProfileDocument> => {
  const response = await api.post(`/api/admin/workers/${workerProfileId}/documents`, payload)
  return (response.data?.data || response.data?.document) as WorkerProfileDocument
}

export const updateWorkerDocumentForManager = async (
  workerProfileId: string,
  documentId: string,
  payload: Partial<WorkerProfileDocument>,
): Promise<WorkerProfileDocument> => {
  const response = await api.patch(`/api/admin/workers/${workerProfileId}/documents/${documentId}`, payload)
  return (response.data?.data || response.data?.document) as WorkerProfileDocument
}

export const downloadWorkerDocumentForManager = async (
  fileUrl: string,
): Promise<{ blob: Blob; filename: string; contentType: string }> => {
  try {
    const response = await api.get(fileUrl, {
      responseType: 'blob',
      headers: { Accept: '*/*' },
    })
    return {
      blob: response.data as Blob,
      filename: filenameFromDisposition(response.headers?.['content-disposition']) || 'worker-document',
      contentType: String(response.headers?.['content-type'] || ''),
    }
  } catch (error: any) {
    const responseBlob = error?.response?.data
    const contentType = String(error?.response?.headers?.['content-type'] || '')
    if (responseBlob instanceof Blob && contentType.includes('application/json')) {
      try {
        const payload = JSON.parse(await responseBlob.text())
        error.response.data = payload
      } catch {
        // Keep the original error if the server returned a non-JSON blob.
      }
    }
    throw error
  }
}

export const verifyWorkerDocument = async (
  workerProfileId: string,
  documentId: string,
  notes?: string,
  skillLinks?: WorkerDocumentSkillLinkPayload[],
): Promise<WorkerProfileDocument> => {
  const payload = {
    ...(notes ? { notes } : {}),
    ...(skillLinks?.length ? { skillLinks } : {}),
  }
  const response = await api.post(`/api/admin/workers/${workerProfileId}/documents/${documentId}/verify`, payload)
  return (response.data?.data || response.data?.document) as WorkerProfileDocument
}

export const replaceWorkerDocumentSkillLinks = async (
  workerProfileId: string,
  documentId: string,
  skillLinks: WorkerDocumentSkillLinkPayload[],
): Promise<{ document: WorkerProfileDocument; progression?: unknown[] }> => {
  const response = await api.put(`/api/admin/workers/${workerProfileId}/documents/${documentId}/skill-links`, { skillLinks })
  return {
    document: (response.data?.data || response.data?.document) as WorkerProfileDocument,
    progression: response.data?.progression || [],
  }
}

export const rejectWorkerDocument = async (
  workerProfileId: string,
  documentId: string,
  reason: string,
  notes?: string,
): Promise<WorkerProfileDocument> => {
  const response = await api.post(`/api/admin/workers/${workerProfileId}/documents/${documentId}/reject`, { reason, notes })
  return (response.data?.data || response.data?.document) as WorkerProfileDocument
}

export const expireWorkerDocument = async (
  workerProfileId: string,
  documentId: string,
): Promise<WorkerProfileDocument> => {
  const response = await api.post(`/api/admin/workers/${workerProfileId}/documents/${documentId}/expire`)
  return (response.data?.data || response.data?.document) as WorkerProfileDocument
}

export const revokeWorkerDocument = async (
  workerProfileId: string,
  documentId: string,
  reason?: string,
): Promise<{ action?: string; message?: string; document: WorkerProfileDocument }> => {
  const response = await api.post(`/api/admin/workers/${workerProfileId}/documents/${documentId}/revoke`, reason ? { reason } : {})
  return { action: response.data?.action, message: response.data?.message, document: (response.data?.data || response.data?.document) as WorkerProfileDocument }
}

export const removeWorkerDocumentForManager = async (
  workerProfileId: string,
  documentId: string,
): Promise<{ action?: string; message?: string; document: WorkerProfileDocument }> => {
  const response = await api.delete(`/api/admin/workers/${workerProfileId}/documents/${documentId}`)
  return { action: response.data?.action, message: response.data?.message, document: (response.data?.data || response.data?.document) as WorkerProfileDocument }
}

export const listJobs = async (filters: ListParams = {}): Promise<Job[]> => {
  const response = await api.get('/api/jobs', { params: filters })
  return unwrapPaginated<Job>(response.data, 'jobs').items
}

export const listJobsPage = async (filters: ListParams = {}, signal?: AbortSignal): Promise<PaginatedResponse<Job>> => {
  const response = await api.get('/api/jobs', { params: filters, signal })
  return unwrapPaginated<Job>(response.data, 'jobs')
}

export const listScheduleShifts = async (filters: ScheduleShiftFilters & Partial<ListQuery> = {}): Promise<ScheduleShift[]> => {
  const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value)))
  const response = await api.get('/api/admin/schedule/shifts', { params })
  return unwrapPaginated<ScheduleShift>(response.data, 'shifts').items
}

export const listScheduleCalendarEvents = async (filters: ScheduleCalendarFilters): Promise<ScheduleCalendarEvent[]> => {
  const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value)))
  const response = await api.get('/api/admin/schedule/calendar', { params })
  return (response.data?.events || []) as ScheduleCalendarEvent[]
}

export const getScheduleShiftDetail = async (shiftId: string): Promise<ScheduleShiftDetail> => {
  const response = await api.get(`/api/admin/schedule/shifts/${shiftId}`)
  return response.data?.detail as ScheduleShiftDetail
}

export const listShiftAssignmentConflicts = async (shiftId: string): Promise<ScheduleShiftConflict[]> => {
  const response = await api.get(`/api/admin/job-shifts/${shiftId}/conflicts`)
  return (response.data?.conflicts || []) as ScheduleShiftConflict[]
}

const filenameFromDisposition = (disposition?: string) => {
  if (!disposition) return ''
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1].replace(/"/g, ''))
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
  return plainMatch?.[1] || ''
}

export const downloadTimesheetExport = async (
  filters: TimesheetExportFilters = {},
): Promise<{ blob: Blob; filename: string }> => {
  const format = filters.format || 'csv'
  const params = Object.fromEntries(Object.entries({ ...filters, format: undefined }).filter(([, value]) => Boolean(value)))
  const response = await api.get(`/api/admin/timesheets/export.${format}`, {
    params,
    responseType: 'blob',
    headers: { Accept: format === 'json' ? 'application/json' : format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : format === 'pdf' ? 'application/pdf' : 'text/csv', 'x-role': 'corporate' },
  })
  return {
    blob: response.data as Blob,
    filename: filenameFromDisposition(response.headers?.['content-disposition']) || `timesheets-export.${format}`,
  }
}

export const downloadTimesheetExportCsv = downloadTimesheetExport

export const previewTimesheetExportCsv = async (
  filters: TimesheetExportFilters = {},
): Promise<string> => {
  const { blob } = await downloadTimesheetExport({ ...filters, format: 'csv', markExported: undefined })
  return blob.text()
}

export const previewTimesheetExportJson = async (
  filters: TimesheetExportFilters = {},
): Promise<Record<string, unknown>> => {
  const { blob } = await downloadTimesheetExport({ ...filters, format: 'json' })
  return JSON.parse(await blob.text()) as Record<string, unknown>
}

export const getOperationsReport = async (
  filters: OperationsReportFilters = {},
): Promise<OperationsReport> => {
  const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value)))
  const response = await api.get('/api/admin/reports/operations', {
    params,
    headers: { 'x-role': 'corporate' },
  })
  return {
    filters: response.data?.filters || filters,
    summary: response.data?.summary,
    byLocation: response.data?.byLocation || [],
    byJob: response.data?.byJob || [],
    attendanceExceptions: response.data?.attendanceExceptions || [],
    approvalQueue: response.data?.approvalQueue || [],
    generatedAt: response.data?.generatedAt || new Date().toISOString(),
  } as OperationsReport
}

export const listReportingExportHistory = async (query: ListQuery = {}): Promise<ReportingExportHistoryRow[]> => {
  const response = await api.get('/api/admin/timesheets/exports/history', {
    params: query,
    headers: { 'x-role': 'corporate' },
  })
  return unwrapPaginated<ReportingExportHistoryRow>(response.data, 'history').items
}

export const listReportingExportHistoryPage = async (query: ListQuery = {}): Promise<PaginatedResponse<ReportingExportHistoryRow>> => {
  const response = await api.get('/api/admin/timesheets/exports/history', {
    params: query,
    headers: { 'x-role': 'corporate' },
  })
  return unwrapPaginated<ReportingExportHistoryRow>(response.data, 'history')
}

export const listTimesheetApprovals = async (
  filters: TimesheetExportFilters = {},
): Promise<TimesheetApprovalQueueRow[]> => {
  const { approvalState, ...rest } = filters
  const params = Object.fromEntries(Object.entries({
    ...rest,
    state: approvalState || undefined,
  }).filter(([, value]) => Boolean(value)))
  const response = await api.get('/api/admin/timesheets/pending-approvals', {
    params,
    headers: { 'x-role': 'corporate' },
  })
  return unwrapPaginated<TimesheetApprovalQueueRow>(response.data, 'approvals').items
}

export const listTimesheetApprovalsPage = async (
  filters: TimesheetExportFilters & Partial<ListQuery> = {},
): Promise<PaginatedResponse<TimesheetApprovalQueueRow>> => {
  const { approvalState, ...rest } = filters
  const params = Object.fromEntries(Object.entries({
    ...rest,
    state: approvalState || undefined,
  }).filter(([, value]) => Boolean(value)))
  const response = await api.get('/api/admin/timesheets/pending-approvals', {
    params,
    headers: { 'x-role': 'corporate' },
  })
  return unwrapPaginated<TimesheetApprovalQueueRow>(response.data, 'approvals')
}

export const approveTimesheet = async (shiftId: string, notes?: string): Promise<TimesheetApprovalQueueRow> => {
  const response = await api.post(`/api/admin/timesheets/${shiftId}/approve`, notes ? { notes } : {}, {
    headers: { 'x-role': 'corporate' },
  })
  return response.data?.approval
}

export const approveCompanyTimesheet = async (shiftId: string, notes?: string): Promise<TimesheetApprovalQueueRow> => {
  const response = await api.post(`/api/admin/timesheets/${shiftId}/approve-company`, notes ? { notes } : {}, {
    headers: { 'x-role': 'corporate' },
  })
  return response.data?.approval
}

export const rejectTimesheet = async (shiftId: string, reason: string): Promise<TimesheetApprovalQueueRow> => {
  const response = await api.post(`/api/admin/timesheets/${shiftId}/reject`, { reason }, {
    headers: { 'x-role': 'corporate' },
  })
  return response.data?.approval
}

export const adjustTimesheet = async (shiftId: string, payload: AdminTimesheetAdjustPayload): Promise<TimesheetApprovalQueueRow> => {
  const response = await api.post(`/api/admin/timesheets/${shiftId}/adjust`, payload, {
    headers: { 'x-role': 'corporate' },
  })
  return response.data?.approval
}

export const resolveTimesheetDispute = async (shiftId: string, payload: AdminTimesheetResolvePayload): Promise<TimesheetApprovalQueueRow> => {
  const response = await api.post(`/api/admin/timesheets/${shiftId}/resolve-dispute`, payload, {
    headers: { 'x-role': 'corporate' },
  })
  return response.data?.approval
}

export const addTimesheetApprovalComment = async (shiftId: string, commentText: string): Promise<unknown> => {
  const response = await api.post(`/api/admin/timesheets/${shiftId}/comments`, { commentText, visibility: 'worker_visible' }, {
    headers: { 'x-role': 'corporate' },
  })
  return response.data?.comment
}

export const listTimesheetApprovalHistory = async (shiftId: string): Promise<TimesheetApprovalHistoryRow[]> => {
  const response = await api.get(`/api/admin/timesheets/${shiftId}/approval-history`, {
    headers: { 'x-role': 'corporate' },
  })
  return (response.data?.history || []) as TimesheetApprovalHistoryRow[]
}

export const listPendingOfflinePunches = async (filters: Record<string, string | undefined> = {}): Promise<OfflinePunchReview[]> => {
  const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value)))
  const response = await api.get('/api/admin/offline-punches/pending', {
    params,
    headers: { 'x-role': 'corporate' },
  })
  return (response.data?.data || response.data?.punches || []) as OfflinePunchReview[]
}

export const listMessageThreads = async (filters: { search?: string; filter?: string } = {}): Promise<MessageThread[]> => {
  const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value)))
  const response = await api.get('/api/messages/threads', { params, headers: { 'x-role': 'corporate' } })
  return (response.data?.threads || []) as MessageThread[]
}

export const createMessageThread = async (payload: CreateMessageThreadRequest): Promise<MessageThread> => {
  const response = await api.post('/api/messages/threads', payload, { headers: { 'x-role': 'corporate' } })
  return response.data?.thread as MessageThread
}

export const getMessageThread = async (threadId: string): Promise<MessageThread> => {
  const response = await api.get(`/api/messages/threads/${threadId}`, { headers: { 'x-role': 'corporate' } })
  return response.data?.thread as MessageThread
}

export const listThreadMessages = async (threadId: string): Promise<Message[]> => {
  const response = await api.get(`/api/messages/threads/${threadId}/messages`, { headers: { 'x-role': 'corporate' } })
  return (response.data?.messages || []) as Message[]
}

export const sendThreadMessage = async (threadId: string, payload: SendMessageRequest): Promise<Message> => {
  const response = await api.post(`/api/messages/threads/${threadId}/messages`, payload, { headers: { 'x-role': 'corporate' } })
  return response.data?.message as Message
}

export const markThreadRead = async (threadId: string): Promise<void> => {
  await api.patch(`/api/messages/threads/${threadId}/read`, {}, { headers: { 'x-role': 'corporate' } })
}

export const archiveThread = async (threadId: string): Promise<void> => {
  await api.patch(`/api/messages/threads/${threadId}/archive`, {}, { headers: { 'x-role': 'corporate' } })
}

export const searchMessageRecipients = async (search = ''): Promise<MessageRecipient[]> => {
  const response = await api.get('/api/messages/recipients/workers', { params: { search }, headers: { 'x-role': 'corporate' } })
  return (response.data?.recipients || []) as MessageRecipient[]
}

export const listLocationMessageRecipients = async (locationId: string): Promise<MessageRecipient[]> => {
  const response = await api.get(`/api/messages/recipients/by-location/${locationId}`, { headers: { 'x-role': 'corporate' } })
  return (response.data?.recipients || []) as MessageRecipient[]
}

export const listJobMessageRecipients = async (jobId: string): Promise<MessageRecipient[]> => {
  const response = await api.get(`/api/messages/recipients/by-job/${jobId}`, { headers: { 'x-role': 'corporate' } })
  return (response.data?.recipients || []) as MessageRecipient[]
}

export const listShiftMessageRecipients = async (shiftId: string): Promise<MessageRecipient[]> => {
  const response = await api.get(`/api/messages/recipients/by-shift/${shiftId}`, { headers: { 'x-role': 'corporate' } })
  return (response.data?.recipients || []) as MessageRecipient[]
}

export const approveOfflinePunch = async (punchEventId: string, notes?: string): Promise<OfflinePunchReview> => {
  const response = await api.post(`/api/admin/offline-punches/${punchEventId}/approve`, notes ? { notes } : {}, {
    headers: { 'x-role': 'corporate' },
  })
  return response.data?.data as OfflinePunchReview
}

export const rejectOfflinePunch = async (punchEventId: string, reason: string, notes?: string): Promise<OfflinePunchReview> => {
  const response = await api.post(`/api/admin/offline-punches/${punchEventId}/reject`, { reason, notes }, {
    headers: { 'x-role': 'corporate' },
  })
  return response.data?.data as OfflinePunchReview
}

export const createJob = async (payload: JobPayload): Promise<Job> => {
  const response = await api.post('/api/jobs', payload)
  return response.data?.job as Job
}

export const createWorkPosting = async (payload: WorkPostingPayload): Promise<WorkPostingResult> => {
  const response = await api.post('/api/admin/work-postings', payload)
  return {
    job: response.data?.job as Job,
    shift: response.data?.shift as JobShift,
    nextAction: response.data?.nextAction,
    delegatedLineage: response.data?.delegatedLineage || null,
  }
}

export const getJob = async (jobId: string): Promise<Job> => {
  const response = await api.get(`/api/jobs/${jobId}`)
  return response.data?.job as Job
}

export const updateJob = async (jobId: string, payload: JobUpdatePayload): Promise<Job> => {
  const response = await api.patch(`/api/jobs/${jobId}`, payload)
  return response.data?.job as Job
}

export const updateJobStatus = async (jobId: string, payload: JobStatusPayload): Promise<Job> => {
  const response = await api.patch(`/api/jobs/${jobId}/status`, payload)
  return response.data?.job as Job
}

export const listJobVisibilityRules = async (jobId: string): Promise<JobVisibilityRule[]> => {
  const response = await api.get(`/api/jobs/${jobId}/visibility`)
  return (response.data?.visibilityRules || []) as JobVisibilityRule[]
}

export const createJobVisibilityRule = async (
  jobId: string,
  payload: JobVisibilityRulePayload,
): Promise<JobVisibilityRule> => {
  const response = await api.post(`/api/jobs/${jobId}/visibility`, payload)
  return response.data?.visibilityRule as JobVisibilityRule
}

export const deleteJobVisibilityRule = async (ruleId: string): Promise<JobVisibilityRule> => {
  const response = await api.delete(`/api/job-visibility-rules/${ruleId}`)
  return response.data?.visibilityRule as JobVisibilityRule
}

export const listJobShifts = async (jobId: string, query: ListQuery = {}): Promise<JobShift[]> => {
  const response = await api.get(`/api/jobs/${jobId}/shifts`, { params: query })
  return unwrapPaginated<JobShift>(response.data, 'shifts').items
}

export const createJobShift = async (jobId: string, payload: JobShiftPayload): Promise<JobShift> => {
  const response = await api.post(`/api/jobs/${jobId}/shifts`, payload)
  return response.data?.shift as JobShift
}

export const updateJobShift = async (shiftId: string, payload: JobShiftUpdatePayload): Promise<JobShift> => {
  const response = await api.patch(`/api/job-shifts/${shiftId}`, payload)
  return response.data?.shift as JobShift
}

export const duplicateJobShift = async (shiftId: string): Promise<JobShift> => {
  const response = await api.post(`/api/job-shifts/${shiftId}/duplicate`)
  return response.data?.shift as JobShift
}

export const cancelJobShift = async (shiftId: string, reason?: string): Promise<JobShift> => {
  const response = await api.patch(`/api/job-shifts/${shiftId}/cancel`, reason ? { cancellationReason: reason } : {})
  return response.data?.shift as JobShift
}

export const publishJobShift = async (
  shiftId: string,
  payload: { visibility?: string; applicationDeadlineAt?: string | null; minWorkers?: number | null; maxWorkers?: number | null; schedulingNotes?: string | null } = {},
): Promise<JobShift> => {
  const response = await api.post(`/api/admin/job-shifts/${shiftId}/publish`, payload)
  return response.data?.shift as JobShift
}

export const listOpenJobMatches = async (): Promise<JobShift[]> => {
  const response = await api.get('/api/jobs/open-matches')
  return (response.data?.matches || []) as JobShift[]
}

export const listJobShiftMatches = async (shiftId: string): Promise<JobShiftWorkerMatch[]> => {
  const response = await api.get(`/api/job-shifts/${shiftId}/matches`)
  return (response.data?.matches || []) as JobShiftWorkerMatch[]
}

export const listJobApplications = async (jobId: string): Promise<JobApplication[]> => {
  const response = await api.get(`/api/jobs/${jobId}/applications`)
  return (response.data?.applications || []) as JobApplication[]
}

export const createJobApplication = async (jobId: string, payload: JobApplicationPayload): Promise<JobApplication> => {
  const response = await api.post(`/api/jobs/${jobId}/applications`, payload)
  return response.data?.application as JobApplication
}

export const updateJobApplicationStatus = async (
  applicationId: string,
  status: JobApplicationStatus,
): Promise<JobApplication> => {
  const response = await api.patch(`/api/job-applications/${applicationId}/status`, { status })
  return response.data?.application as JobApplication
}

export const listJobAssignments = async (filters: ListParams = {}): Promise<JobAssignment[]> => {
  const response = await api.get('/api/job-assignments', { params: filters })
  return unwrapPaginated<JobAssignment>(response.data, 'assignments').items
}

export const listJobAssignmentsPage = async (filters: ListParams = {}): Promise<PaginatedResponse<JobAssignment>> => {
  const response = await api.get('/api/job-assignments', { params: filters })
  return unwrapPaginated<JobAssignment>(response.data, 'assignments')
}

export const createJobAssignment = async (payload: JobAssignmentPayload): Promise<JobAssignment> => {
  const response = await api.post('/api/job-assignments', payload)
  return { ...(response.data?.assignment || {}), warnings: response.data?.warnings || [] } as JobAssignment
}

export const listRankedShiftApplicants = async (shiftId: string): Promise<ShiftApplication[]> => {
  const response = await api.get(`/api/admin/job-shifts/${shiftId}/ranked-applicants`)
  return (response.data?.applicants || []) as ShiftApplication[]
}

export const approveShiftApplication = async (applicationId: string): Promise<{ application: ShiftApplication; assignment?: JobAssignment }> => {
  const response = await api.post(`/api/admin/shift-applications/${applicationId}/approve`)
  return { application: response.data?.application as ShiftApplication, assignment: response.data?.assignment as JobAssignment | undefined }
}

export const rejectShiftApplication = async (applicationId: string, notes?: string): Promise<ShiftApplication> => {
  const response = await api.post(`/api/admin/shift-applications/${applicationId}/reject`, notes ? { notes } : {})
  return response.data?.application as ShiftApplication
}

export const updateJobAssignment = async (
  assignmentId: string,
  payload: JobAssignmentUpdatePayload,
): Promise<JobAssignment> => {
  const response = await api.patch(`/api/job-assignments/${assignmentId}`, payload)
  return response.data?.assignment as JobAssignment
}

export const updateJobAssignmentStatus = async (
  assignmentId: string,
  payload: JobAssignmentStatusPayload,
): Promise<JobAssignment> => {
  const response = await api.patch(`/api/job-assignments/${assignmentId}/status`, payload)
  return response.data?.assignment as JobAssignment
}

export const approveJobAssignment = async (assignmentId: string): Promise<JobAssignment> => {
  const response = await api.post(`/api/job-assignments/${assignmentId}/approve`)
  return response.data?.assignment as JobAssignment
}

export const acceptJobAssignment = async (assignmentId: string): Promise<JobAssignment> => {
  const response = await api.post(`/api/job-assignments/${assignmentId}/accept`)
  return response.data?.assignment as JobAssignment
}

export const acceptJobAssignmentAsWorker = acceptJobAssignment

export const listSkills = async (): Promise<Skill[]> => {
  const response = await api.get('/api/skills')
  return (response.data?.skills || []) as Skill[]
}

export const createSkill = async (payload: SkillPayload): Promise<Skill> => {
  const response = await api.post('/api/skills', payload)
  return response.data?.skill as Skill
}

export const accessService = {
  getAccessContext,
  listOrganizations,
  listOrganizationsPage,
  createOrganization,
  createAssociatedClientCompany,
  updateOrganization,
  listSystemTenants,
  createSystemTenant,
  updateSystemTenantIndustry,
  getSystemOrganizationDeleteImpact,
  runSystemOrganizationLifecycleAction,
  getSystemWorkerDeleteImpact,
  runSystemWorkerLifecycleAction,
  getSystemUserDeleteImpact,
  runSystemUserLifecycleAction,
  getJobDeleteImpact,
  archiveJob,
  cancelJob,
  restoreJob,
  deleteJob,
  purgeDemoJob,
  getJobShiftDeleteImpact,
  archiveJobShift,
  cancelSystemJobShift,
  restoreJobShift,
  deleteJobShift,
  purgeDemoJobShift,
  getJobAssignmentDeleteImpact,
  cancelJobAssignment,
  revokeJobAssignment,
  restoreJobAssignment,
  deleteJobAssignment,
  purgeDemoJobAssignment,
  getLocationDeleteImpact,
  disableLocation,
  archiveLocation,
  restoreLocation,
  deleteLocation,
  purgeDemoLocation,
  getLocationGeofenceDeleteImpact,
  disableLocationGeofence,
  clearLocationGeofence,
  restoreLocationGeofence,
  listOrganizationRelationships,
  createOrganizationRelationship,
  updateOrganizationRelationship,
  searchOrganizationsForRelationship,
  createOrganizationRelationshipRequest,
  listIncomingOrganizationRelationshipRequests,
  listOutgoingOrganizationRelationshipRequests,
  acceptOrganizationRelationshipRequest,
  rejectOrganizationRelationshipRequest,
  createOrganizationConnectionInvite,
  listOrganizationConnectionInvites,
  redeemOrganizationConnectionInvite,
  revokeOrganizationConnectionInvite,
  searchSystemOrganizations,
  createSystemOrganizationRelationship,
  listOrganizationUnits,
  createOrganizationUnit,
  updateOrganizationUnit,
  getWhatsappNotificationStatus,
  updateWhatsappProviderSettings,
  archiveOrgUnit,
  restoreOrgUnit,
  getOrganizationBranding,
  updateOrganizationBranding,
  createScopedRoleAssignment,
  updateScopedRoleAssignment,
  disableScopedRoleAssignment,
  listOrganizationUsers,
  listOrganizationUsersPage,
  listOrganizationInvites,
  createOrganizationUser,
  listOrganizationRoles,
  listAdminCapabilities,
  createTenantRole,
  updateTenantRole,
  deleteTenantRole,
  inviteOrganizationUser,
  updateOrganizationUser,
  disableOrganizationUser,
  resetOrganizationUserPassword,
  revokeOrganizationInvite,
  listOrganizationUserAccess,
  assignOrganizationUserRole,
  revokeOrganizationUserRole,
  listOrgWorkers,
  inviteOrgWorker,
  associateOrgWorker,
  shareOrgWorker,
  removeOrgWorker,
  createWorkerOrgRelationship,
  createWorkerRelationship,
  updateWorkerOrgRelationship,
  updateWorkerRelationship,
  endWorkerOrgRelationship,
  endWorkerRelationship,
  listFreeAgents,
  listFreeAgentWorkers,
  updateWorkerProfileVisibility,
  listWorkerProfiles,
  listWorkerProfilesPage,
  getWorkerProfile,
  getWorkerReliability,
  getWorkerAvailabilityForAdmin,
  listWorkerAttendanceEventsForAdmin,
  listWorkerReviewsForAdmin,
  listMyAvailability,
  createMyAvailability,
  updateMyAvailability,
  deleteMyAvailability,
  listMyUnavailability,
  createMyUnavailability,
  deleteMyUnavailability,
  listMyOpenShifts,
  listRecommendedOpenShifts,
  applyToOpenShift,
  listMyShiftApplications,
  withdrawMyShiftApplication,
  updateWorkerProfile,
  listWorkerSkills,
  upsertWorkerSkill,
  deleteWorkerSkill,
  listSharingAgreementsForOrg,
  listSharingAgreements,
  listSharingAgreementsPage,
  createSharingAgreement,
  getSharingAgreement,
  updateSharingAgreement,
  addSharingAgreementResource,
  removeSharingAgreementResource,
  listWorkerDocumentConsents,
  listWorkerDocumentConsentsForWorker,
  createWorkerDocumentConsent,
  updateWorkerDocumentConsent,
  listWorkerDocumentsForManager,
  listWorkerDocumentRisks,
  createWorkerDocumentForManager,
  updateWorkerDocumentForManager,
  downloadWorkerDocumentForManager,
  verifyWorkerDocument,
  rejectWorkerDocument,
  expireWorkerDocument,
  revokeWorkerDocument,
  removeWorkerDocumentForManager,
  getOnboardingStatus,
  startOnboarding,
  getOnboardingReadiness,
  updateOnboardingStatus,
  saveOnboardingCompanyProfile,
  saveOnboardingLocations,
  saveOnboardingGeofence,
  saveOnboardingSupervisors,
  previewOnboardingWorkerImport,
  saveOnboardingWorkers,
  saveOnboardingFirstJob,
  completeOnboarding,
  skipOnboarding,
  listScheduleShifts,
  listScheduleCalendarEvents,
  getScheduleShiftDetail,
  listShiftAssignmentConflicts,
  downloadTimesheetExportCsv,
  downloadTimesheetExport,
  previewTimesheetExportCsv,
  previewTimesheetExportJson,
  getOperationsReport,
  listReportingExportHistory,
  listReportingExportHistoryPage,
  listTimesheetApprovalsPage,
  approveTimesheet,
  approveCompanyTimesheet,
  rejectTimesheet,
  adjustTimesheet,
  resolveTimesheetDispute,
  addTimesheetApprovalComment,
  listTimesheetApprovalHistory,
  listPendingOfflinePunches,
  approveOfflinePunch,
  rejectOfflinePunch,
  listMessageThreads,
  createMessageThread,
  getMessageThread,
  listThreadMessages,
  sendThreadMessage,
  markThreadRead,
  archiveThread,
  searchMessageRecipients,
  listLocationMessageRecipients,
  listJobMessageRecipients,
  listShiftMessageRecipients,
  listJobs,
  listJobsPage,
  createJob,
  getJob,
  updateJob,
  updateJobStatus,
  listJobVisibilityRules,
  createJobVisibilityRule,
  deleteJobVisibilityRule,
  listJobShifts,
  createJobShift,
  updateJobShift,
  duplicateJobShift,
  cancelJobShift,
  publishJobShift,
  listOpenJobMatches,
  listJobShiftMatches,
  listRankedShiftApplicants,
  approveShiftApplication,
  rejectShiftApplication,
  listJobApplications,
  createJobApplication,
  updateJobApplicationStatus,
  listJobAssignments,
  listJobAssignmentsPage,
  createJobAssignment,
  updateJobAssignment,
  updateJobAssignmentStatus,
  approveJobAssignment,
  acceptJobAssignment,
  acceptJobAssignmentAsWorker,
  listSkills,
  createSkill,
  createWorkerInviteCode,
  listWorkerInviteCodes,
  revokeWorkerInviteCode,
  getAutoPunchSettings,
  updateAutoPunchSettings,
}
